import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
    Calendar,
    CalendarClock,
    CheckCircle2,
    DollarSign,
    ExternalLink,
    Flag,
    Loader2,
    MapPin,
    MapPinOff,
    MessageCircle,
    Navigation,
    Phone,
    Route as RouteIcon,
    Search,
    Trash2,
    UserPlus,
    X,
    Zap
} from 'lucide-react';
import { supabase } from '../../../src/lib/supabase';
import { logUsageEvent } from '../../../src/lib/logger';
import { registrarRemanejamentos } from '../../../src/lib/remanejamento';
import { useAuth } from '../../../src/contexts/AuthContext';
import { useToast } from '../../../src/contexts/ToastContext';
import { MapaLead, Profile } from '../../../types';
import LoadingSpinner from '../../../src/components/LoadingSpinner';
import ResponsibleFilter from '../../../src/components/ResponsibleFilter';
import CalendarioMensal, { DiaResumo, TIPO_VISITA_LABELS } from './CalendarioMensal';
import VisitaModal, { VisitaParadaInfo, VisitaConclusao, statusVisitaLabel, proximaAcaoLabel } from './VisitaModal';
import ProdutividadeDashboard from './ProdutividadeDashboard';
import CapturasPanel from './CapturasPanel';
import AderenciaPanel from './AderenciaPanel';
import OperacionalDashboard from './OperacionalDashboard';
import RotasApiLogModal from './RotasApiLogModal';
import RotaDetalheModal from './RotaDetalheModal';
import AlterarDataModal from './AlterarDataModal';
import ReagendarVisitaPopover from './ReagendarVisitaPopover';
import AtribuirVendedorModal from './AtribuirVendedorModal';
import NegotiationDetailModal from '../components/NegotiationDetails/NegotiationDetailModal';
import ConfirmModal from '../../../src/components/ConfirmModal';
import { haversineKm } from '../../../src/lib/routeOptimizer';
import { getLeadMapColor, getLeadMapLabel } from '../../../src/lib/crmVisuals';
import RotaExecucaoCard from './RotaExecucaoCard';
import AgendaSemana from './AgendaSemana';
import { gerarRotaInteligente, recalcularRotaPlanejada, anexarERecalcularPendentes, reordenarPendentesAPartirDe, hojeLocalIso, type PontoRoteirizavel } from '../../../src/lib/rotaInteligente';
import { concluirVisitaTarefa, concluirTarefaComResultado, textoResultado } from '../../../src/lib/visitaTarefa';
import { useAgendaRealtime } from '../../../src/hooks/useAgendaRealtime';
import { GERAR_ROTA_TERCEIROS_PERMISSION, HORA_CORTE_META, META_CONCLUIDAS_DIA, Parada, RotaAtiva, metaChipClass, statusRotaBadge, statusRotaLabel, urlNavegacao } from './rotaTypes';
import { Modalidade, isPresencial, isRemota, modalidadeEmoji, modalidadeLabel, normalizarModalidade } from '../../../src/lib/modalidade';

interface TarefaDia {
    id: number;
    titulo: string;
    descricao?: string | null;
    prioridade: string;
    status: string;
    tipo?: string | null;
    objetivo?: string | null;
    hora_visita?: string | null;
    modalidade?: string | null;
    responsavel_id?: string | null;
    negociacao_id?: number | null;
    negociacoes?: { id: number; titulo: string; latitude?: number | null; longitude?: number | null; cidade?: string | null; bairro?: string | null; consumo_medio_kwh?: number | null; whatsapp?: string | null } | null;
}

interface TarefaMes {
    id: number;
    vencimento: string;
    tipo?: string | null;
    modalidade?: string | null;
    status: string;
    negociacoes?: { consumo_medio_kwh?: number | null } | null;
}

const paradaParaInfo = (p: Parada): VisitaParadaInfo => ({
    id: p.id,
    titulo: p.negociacoes?.titulo || `Parada ${p.posicao}`,
    checkin_em: p.checkin_em,
    cidade: p.negociacoes?.cidade,
    bairro: p.negociacoes?.bairro,
    segmento: p.negociacoes?.segmento,
    telefone: p.negociacoes?.whatsapp,
    kwh: p.negociacoes?.consumo_medio_kwh,
    modalidade: normalizarModalidade(p.modalidade),
    negociacaoId: p.negociacao_id ?? null,
    // Habilita os chips de resultado quando a parada veio de uma visita de pós-venda.
    tipo: (Array.isArray(p.tarefas) ? (p.tarefas as any)[0]?.tipo : p.tarefas?.tipo) ?? null,
});

const hoje = hojeLocalIso;
const addDiasIso = (n: number) => { const d = new Date(); d.setDate(d.getDate() + n); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`; };

const prioridadeBadge = (p: string) => {
    switch (p) {
        case 'alta': return 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400';
        case 'media': return 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400';
        default: return 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400';
    }
};

const RotasView: React.FC = () => {
    const { profile } = useAuth();
    const { addToast } = useToast();

    const isManager = profile?.role === 'adm' || profile?.role === 'gerente';
    // Concessão nominal: gera rota para outros vendedores sem virar gestor —
    // não abre as abas de gestão, só o seletor de vendedores da Agenda.
    const podeGerarParaTerceiros = isManager || !!profile?.permissions?.includes(GERAR_ROTA_TERCEIROS_PERMISSION);
    // Motoboy (operador de campo): só executa rotas com leitura de medidor (foto obrigatória)
    const isMotoboy = profile?.role === 'motoboy';
    const modoVisita = isMotoboy ? 'foto' as const : 'comercial' as const;

    // Abas: 'diario' + 'roteirizacao' foram unificadas em 'agenda' (spec Bruno). Migra o valor salvo.
    // Vendedor vê só a Agenda (rotas ativas moram nela); Produtividade/Capturas/Aderência/Operacional
    // são painéis de gestão e ficam restritos a adm/gerente.
    type RotasTab = 'agenda' | 'produtividade' | 'capturas' | 'aderencia' | 'operacional';
    const TABS_GESTAO: RotasTab[] = ['produtividade', 'capturas', 'aderencia', 'operacional'];
    const [tab, setTab] = useState<RotasTab>(() => {
        const saved = localStorage.getItem('vigorhub_rotas_tab');
        // 'rotas' (aba antiga, unificada na Agenda), 'diario' e 'roteirizacao' caem na Agenda.
        // 'posvenda' virou tela própria em /crm/pos-venda — quem tinha a aba salva volta p/ Agenda.
        if (saved === 'diario' || saved === 'roteirizacao' || saved === 'rotas' || saved === 'posvenda' || !saved) return 'agenda';
        return saved as RotasTab;
    });
    // Sanitiza aba salva: quem não é gestor cai na Agenda se o valor salvo for painel de gestão.
    const tabEfetiva: RotasTab = !isManager && TABS_GESTAO.includes(tab) ? 'agenda' : tab;
    // Zoom da Agenda: Semana (planejamento do vendedor) × Mês (visão macro do gestor).
    const [zoom, setZoom] = useState<'semana' | 'mes'>(() =>
        (localStorage.getItem('vigorhub_rotas_zoom') as 'semana' | 'mes') || ((profile?.role === 'adm' || profile?.role === 'gerente') ? 'mes' : 'semana'));
    const [dataSel, setDataSel] = useState(hoje());
    const [mesAtual, setMesAtual] = useState(() => { const d = new Date(); return new Date(d.getFullYear(), d.getMonth(), 1); });
    const [vendedoresSel, setVendedoresSel] = useState<string[]>([]); // [] = minhas rotas · multi = ver/gerar de vários
    const [profiles, setProfiles] = useState<Profile[]>([]);
    // Seletor de responsável das Rotas lista só quem executa rotas em campo
    // (não polui com Financeiro/Marketing/Tecnologia). A lista completa de `profiles`
    // continua valendo p/ resolver nomes nos painéis e logs.
    // Inclui parceiros: o gestor precisa enxergar/selecionar a carteira dos parceiros na roteirização.
    const ROLES_CAMPO: string[] = ['adm', 'gerente', 'vendedor', 'motoboy', 'vendedor_parceiro', 'parceiro'];
    const profilesCampo = useMemo(
        () => profiles.filter(p => ROLES_CAMPO.includes((p as any).role)),
        [profiles]
    );
    const [tarefas, setTarefas] = useState<TarefaDia[]>([]);
    const [tarefasMes, setTarefasMes] = useState<TarefaMes[]>([]);
    // Status de execução por visita do dia (de rota_paradas), indexado por tarefa_id.
    // Permite mostrar Visitado / Em visita / Não visitado direto no calendário.
    const [paradasDia, setParadasDia] = useState<Map<number, { id: string; status: string; rota_id: string }>>(new Map());
    const [leads, setLeads] = useState<MapaLead[]>([]);
    const [selecionados, setSelecionados] = useState<number[]>(() => {
        try { return JSON.parse(localStorage.getItem('vigorhub_rotas_selecionados') || '[]'); } catch { return []; }
    });
    const [rotasAtivas, setRotasAtivas] = useState<RotaAtiva[]>([]);
    const [loading, setLoading] = useState(true);
    const [gerando, setGerando] = useState(false);
    // Geocodificação sob demanda dos leads "sem localização" (destrava a rota)
    const [geocodificando, setGeocodificando] = useState(false);
    const [geoProgresso, setGeoProgresso] = useState('');
    const [visitaModal, setVisitaModal] = useState<{ parada: VisitaParadaInfo; etapa: 'iniciar' | 'encerrar'; rota: RotaAtiva } | null>(null);
    const [showApiLog, setShowApiLog] = useState(false);
    // Abrir o lead a partir da rota/visita (doc 03 §3.1) — mesmo modal de detalhe do Kanban
    const [leadModalId, setLeadModalId] = useState<number | null>(null);
    // Reagendar visitas não concluídas ao concluir a rota (doc 03 §5)
    const [reagendarRota, setReagendarRota] = useState<{ rota: RotaAtiva; pendentes: Parada[] } | null>(null);
    const [reagendarData, setReagendarData] = useState('');
    // Reagendamento inline de uma única visita do calendário (popover sobreposto)
    const [reagendarVisita, setReagendarVisita] = useState<{ tarefa: TarefaDia; anchor: DOMRect } | null>(null);
    const [reagendandoInline, setReagendandoInline] = useState(false);

    // Seleção múltipla de atividades do dia (Roteiro Diário) + ações em lote
    const [tarefasSel, setTarefasSel] = useState<number[]>([]);
    const [bulkAcao, setBulkAcao] = useState<'data' | 'vendedor' | 'remover' | null>(null);
    const [bulkSalvando, setBulkSalvando] = useState(false);
    const [detalheRotaId, setDetalheRotaId] = useState<string | null>(null);
    // Rota recém-criada (destaque visual) + aviso não-bloqueante de nova visita numa rota em execução
    const [rotaDestaqueId, setRotaDestaqueId] = useState<string | null>(null);
    const [avisoNovaVisita, setAvisoNovaVisita] = useState<{ rotaId: string; qtd: number } | null>(null);
    const [kmPorDia, setKmPorDia] = useState<Record<string, number>>({});
    // Sinal p/ a Agenda Semana recarregar junto com o mês (realtime, reagendamento, ações em lote):
    // garante que Mês e Semana leiam sempre a mesma verdade.
    const [reloadTick, setReloadTick] = useState(0);
    // Barra de indicadores do gestor (spec §4A): agrega por dia, pela semana exibida ou pelo mês exibido.
    const [kpiScope, setKpiScope] = useState<'dia' | 'semana' | 'mes'>('dia');
    // Meta do gestor (adendo §4): "Vendedores abaixo da meta hoje: X de N".
    const [metaEquipe, setMetaEquipe] = useState<{ abaixo: number; total: number } | null>(null);
    // Conclusão de visita do painel do dia — sempre pelo modal "Concluir Visita" (pedido Bruno 10/jul)
    const [visitaTarefaModal, setVisitaTarefaModal] = useState<TarefaDia | null>(null);
    const sincronizandoRef = useRef(false);

    const selSet = useMemo(() => new Set(tarefasSel), [tarefasSel]);
    const tarefasSelObjs = useMemo(() => tarefas.filter(t => selSet.has(t.id)), [tarefas, selSet]);
    const kwhSel = useMemo(() => tarefasSelObjs.reduce((s, t) => s + Number(t.negociacoes?.consumo_medio_kwh || 0), 0), [tarefasSelObjs]);
    const selRoteirizaveisCount = useMemo(
        () => tarefasSelObjs.filter(t => t.negociacoes?.latitude && t.negociacoes?.longitude && t.negociacao_id).length,
        [tarefasSelObjs]
    );
    const todasSelecionadas = tarefas.length > 0 && tarefasSel.length === tarefas.length;
    const algumasSelecionadas = tarefasSel.length > 0 && !todasSelecionadas;

    const toggleSelTarefa = (id: number) => setTarefasSel(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
    const toggleSelTodas = () => setTarefasSel(todasSelecionadas ? [] : tarefas.map(t => t.id));
    const limparSel = () => setTarefasSel([]);

    // Filtros da roteirização
    const [search, setSearch] = useState('');
    const [consumoMin, setConsumoMin] = useState('');

    // Responsáveis efetivos: gestores podem ver/gerar em nome de vários. Vazio = só eu.
    const vendedoresEfetivos = useMemo(
        () => (vendedoresSel.length ? vendedoresSel : (profile ? [profile.id] : [])),
        [vendedoresSel, profile?.id]
    );
    const multi = vendedoresEfetivos.length > 1;
    // Vendedor "principal" (contexto de seleção única): usado na execução e na geração de rota única
    const vendedorId = vendedoresEfetivos[0] || profile?.id || '';
    // Para sub-painéis (Produtividade/Capturas/Aderência/Operacional) que filtram por 1 vendedor
    const vendedorSelLegacy = vendedoresSel.length === 1 ? vendedoresSel[0] : '';
    // Contexto single (um responsável): renderiza TODAS as rotas ativas dele como cards empilhados.
    // Multi (gestor c/ vários): painel vira resumo somente-leitura (sem execução).
    const nomeVendedor = (id: string) => profiles.find(p => String(p.id) === String(id))?.full_name || '—';

    useEffect(() => { localStorage.setItem('vigorhub_rotas_tab', tab); }, [tab]);
    useEffect(() => { localStorage.setItem('vigorhub_rotas_zoom', zoom); }, [zoom]);
    useEffect(() => { localStorage.setItem('vigorhub_rotas_selecionados', JSON.stringify(selecionados)); }, [selecionados]);
    // Mantém a seleção de atividades do dia válida quando a lista recarrega (troca de data/vendedor)
    useEffect(() => { setTarefasSel(prev => prev.filter(id => tarefas.some(t => t.id === id))); }, [tarefas]);
    // Carrega o status de execução (rota_paradas) sempre que a lista de visitas do dia muda
    useEffect(() => { fetchParadasDia(tarefas.map(t => t.id)); }, [tarefas]);

    const fetchTarefas = async (vids: string[]) => {
        if (vids.length === 0) { setTarefas([]); setParadasDia(new Map()); return; }
        // Inclui 'concluido' para exibir as visitas já realizadas no dia (selo "Visitado").
        // 'cancelado' fica de fora (saiu do roteiro).
        const { data, error } = await supabase
            .from('tarefas')
            .select('id, titulo, descricao, prioridade, status, tipo, objetivo, hora_visita, modalidade, responsavel_id, negociacao_id, negociacoes(id, titulo, latitude, longitude, cidade, bairro, consumo_medio_kwh, whatsapp)')
            .eq('vencimento', dataSel)
            .in('responsavel_id', vids)
            .in('status', ['a_iniciar', 'em_andamento', 'aguardando', 'concluido'])
            .order('hora_visita', { ascending: true, nullsFirst: false });
        if (error) { console.error(error); return; }
        setTarefas((data || []) as unknown as TarefaDia[]);
    };

    // Status de execução por visita (rota_paradas) das tarefas do dia, p/ refletir o
    // que está registrado no card do lead: Visitado / Em visita / Não visitado.
    const fetchParadasDia = async (tarefaIds: number[]) => {
        if (tarefaIds.length === 0) { setParadasDia(new Map()); return; }
        const { data, error } = await supabase
            .from('rota_paradas')
            .select('id, status, rota_id, tarefa_id')
            .in('tarefa_id', tarefaIds);
        if (error) { console.error(error); return; }
        const map = new Map<number, { id: string; status: string; rota_id: string }>();
        for (const p of (data || []) as any[]) {
            if (p.tarefa_id != null) map.set(Number(p.tarefa_id), { id: p.id, status: p.status, rota_id: p.rota_id });
        }
        setParadasDia(map);
    };

    // Status consolidado da visita: em_visita > visitado/concluída > roteirizada (na rota, não iniciada) > pendente
    const statusVisita = (t: TarefaDia): 'visitado' | 'em_visita' | 'roteirizada' | 'pendente' => {
        const p = paradasDia.get(t.id);
        if (p?.status === 'em_visita') return 'em_visita';
        if (p?.status === 'visitado' || t.status === 'concluido') return 'visitado';
        if (p?.status === 'pendente') {
            const rota = rotasAtivas.find(r => r.id === p.rota_id);
            if (rota && rota.status !== 'em_execucao') return 'roteirizada';
        }
        return 'pendente';
    };

    const fetchTarefasMes = async (vids: string[], mes: Date) => {
        if (vids.length === 0) { setTarefasMes([]); return; }
        const ini = `${mes.getFullYear()}-${String(mes.getMonth() + 1).padStart(2, '0')}-01`;
        const fimDate = new Date(mes.getFullYear(), mes.getMonth() + 1, 0);
        const fim = `${fimDate.getFullYear()}-${String(fimDate.getMonth() + 1).padStart(2, '0')}-${String(fimDate.getDate()).padStart(2, '0')}`;
        const { data, error } = await supabase
            .from('tarefas')
            .select('id, vencimento, tipo, modalidade, status, negociacoes(consumo_medio_kwh)')
            .in('responsavel_id', vids)
            .gte('vencimento', ini)
            .lte('vencimento', fim)
            .neq('status', 'cancelado');
        if (error) { console.error(error); return; }
        setTarefasMes((data || []) as unknown as TarefaMes[]);
    };

    // Km estimado das rotas por dia (badge do calendário no modo Mês — visão do gestor)
    const fetchRotasMesResumo = async (vids: string[], mes: Date) => {
        if (vids.length === 0) { setKmPorDia({}); return; }
        const ini = `${mes.getFullYear()}-${String(mes.getMonth() + 1).padStart(2, '0')}-01`;
        const fimDate = new Date(mes.getFullYear(), mes.getMonth() + 1, 0);
        const fim = `${fimDate.getFullYear()}-${String(fimDate.getMonth() + 1).padStart(2, '0')}-${String(fimDate.getDate()).padStart(2, '0')}`;
        const { data, error } = await supabase
            .from('rotas_comerciais')
            .select('data, distancia_total_km, status')
            .in('vendedor_id', vids)
            .neq('status', 'cancelada')
            .gte('data', ini)
            .lte('data', fim);
        if (error) { console.error(error); return; }
        const map: Record<string, number> = {};
        for (const r of (data || []) as { data: string; distancia_total_km: number | null }[]) {
            const k = (r.data || '').slice(0, 10);
            if (!k) continue;
            map[k] = (map[k] || 0) + Number(r.distancia_total_km || 0);
        }
        setKmPorDia(map);
    };

    /**
     * Geocodifica os leads que têm cidade/endereço mas ainda não têm coordenada —
     * sem coordenada eles não entram em rota. Usa a edge function `geocode-leads`
     * (OpenStreetMap/Nominatim, grátis) em lotes até esgotar a fila.
     */
    const geocodificarPendentes = async () => {
        setGeocodificando(true);
        setGeoProgresso('');
        let total = 0;
        try {
            for (let i = 0; i < 40; i++) { // teto de segurança: 40 lotes de 25 = 1000 leads
                const { data, error } = await supabase.functions.invoke('geocode-leads', { body: { batch: 25 } });
                if (error) throw error;
                const n = (data?.processed || []).length;
                if (n === 0) break;
                total += n;
                setGeoProgresso(`${total}`);
            }
            await fetchLeads();
            addToast(total > 0
                ? `${total} lead(s) localizados — já dá para incluir na rota 📍`
                : 'Nenhum lead pendente com endereço suficiente para localizar.', total > 0 ? 'success' : 'info');
            if (total > 0) logUsageEvent({ userId: profile?.id, modulo: 'crm', funcionalidade: 'rotas', acao: 'geocodificar_leads', origem: 'acao' });
        } catch (e: any) {
            console.error(e);
            addToast('Erro ao localizar leads: ' + (e.message || ''), 'error');
        } finally {
            setGeocodificando(false);
            setGeoProgresso('');
        }
    };

    const fetchLeads = async () => {
        // Carteira COMPLETA do funil (inclusive leads sem localização — marcados e não-roteáveis na lista).
        // O PostgREST corta cada resposta em 1000 linhas; paginamos com .range() até
        // esgotar, senão a carteira acima de 1000 sumia (só apareciam os de maior consumo).
        const build = () => {
            let q = supabase
                .from('vw_mapa_leads')
                .select('*')
                .order('consumo_medio_kwh', { ascending: false });
            if (profile?.role === 'vendedor' || profile?.role === 'vendedor_parceiro') {
                q = q.eq('responsavel_id', profile.id);
            } else if (vendedoresSel.length > 0) {
                q = q.in('responsavel_id', vendedoresSel);
            }
            return q;
        };
        const PAGE = 1000;
        const acc: MapaLead[] = [];
        for (let from = 0; ; from += PAGE) {
            const { data, error } = await build().range(from, from + PAGE - 1);
            if (error) { console.error(error); break; }
            const rows = (data || []) as MapaLead[];
            acc.push(...rows);
            if (rows.length < PAGE) break;
        }
        setLeads(acc);
    };

    const fetchRotasAtivas = async (vids: string[]) => {
        if (vids.length === 0) { setRotasAtivas([]); return; }
        // Só hoje/futuro (rotas antigas não são "histórico" aqui — adendo Bruno §1);
        // rota em execução aparece sempre, mesmo se começou noutro dia.
        const { data, error } = await supabase
            .from('rotas_comerciais')
            .select('id, data, status, origem, vendedor_id, nome, prioridade, observacoes, distancia_total_km, duracao_min, gerada_em, snapshot_otimizacao, destino_lat, destino_lng, destino_label, rota_paradas(id, posicao, status, checkin_em, checkout_em, descricao_visita, status_visita, proxima_acao, foto_url, modalidade, negociacao_id, tarefa_id, tarefas(tipo), negociacoes(id, titulo, latitude, longitude, geocode_status, cidade, bairro, segmento, consumo_medio_kwh, whatsapp, unidades_consumidoras(logradouro, numero, bairro, cidade, cep)))')
            .in('vendedor_id', vids)
            .in('status', ['planejada', 'agendada', 'reagendada', 'em_execucao'])
            .or(`data.gte.${hoje()},status.eq.em_execucao`)
            .order('created_at', { ascending: false });
        if (error) { console.error(error); return; }
        const rotas = (data || []) as unknown as RotaAtiva[];
        rotas.forEach(r => r.rota_paradas.sort((a, b) => a.posicao - b.posicao));
        // Mantém TODAS as rotas ativas (sem colapsar por vendedor) — o vendedor pode ter várias ao mesmo tempo.
        setRotasAtivas(rotas);
    };

    const refreshAll = async (vids: string[]) => {
        await Promise.all([fetchTarefas(vids), fetchLeads(), fetchRotasAtivas(vids), fetchTarefasMes(vids, mesAtual), fetchRotasMesResumo(vids, mesAtual)]);
        setReloadTick(t => t + 1);
    };

    useEffect(() => {
        const init = async () => {
            if (!profile) return;
            setLoading(true);
            if (podeGerarParaTerceiros) {
                const { data: profs } = await supabase.from('profiles').select('*').neq('role', 'consumidor');
                if (profs) setProfiles(profs.filter((p: Profile) => (p as any).status !== 'inactive'));
            }
            await refreshAll(vendedoresEfetivos);
            setLoading(false);
        };
        init();
    }, [profile?.id]);

    useEffect(() => {
        if (profile && !loading) refreshAll(vendedoresEfetivos);
    }, [dataSel, vendedoresSel]);

    useEffect(() => {
        if (profile && !loading) { fetchTarefasMes(vendedoresEfetivos, mesAtual); fetchRotasMesResumo(vendedoresEfetivos, mesAtual); }
    }, [mesAtual]);

    // ── Rota Inteligente: sincronização com o que está planejado (spec §3.2) ──
    // Recalcula automaticamente a rota planejada do próprio usuário quando o conjunto de
    // visitas do dia muda; se a rota já está em execução, só avisa (não recomputa sozinho).
    const tarefaParaPontoRot = (t: TarefaDia): PontoRoteirizavel => ({
        id: String(t.id), lat: t.negociacoes!.latitude!, lng: t.negociacoes!.longitude!,
        label: t.negociacoes?.titulo || t.titulo, negociacao_id: t.negociacao_id!, tarefa_id: t.id,
    });

    const sincronizarRotaDoDia = async () => {
        if (!profile || sincronizandoRef.current) return;
        const minhas = tarefas.filter(t => tarefaRoteirizavel(t) && String(t.responsavel_id) === String(profile.id));
        // Qualquer rota ativa do dia (não só a 'inteligente') é atualizada no lugar — nunca
        // se cria uma segunda rota para o mesmo dia (adendo Bruno §1).
        const rota = rotasAtivas.find(r =>
            r.data === dataSel && String(r.vendedor_id) === String(profile.id) &&
            ['planejada', 'agendada', 'reagendada', 'em_execucao'].includes(r.status));
        if (!rota) { setAvisoNovaVisita(null); return; }
        const paradaIds = new Set(rota.rota_paradas.map(p => p.tarefa_id).filter(Boolean) as number[]);
        const novas = minhas.filter(t => !paradaIds.has(t.id));

        if (rota.status !== 'em_execucao') {
            const mesmoConjunto = minhas.length === paradaIds.size && minhas.every(t => paradaIds.has(t.id));
            if (mesmoConjunto || minhas.length === 0) return;
            sincronizandoRef.current = true;
            try {
                // Reusa a origem original do snapshot (sem novo prompt de GPS a cada evento).
                const snapOrigin = (rota as any).snapshot_otimizacao?.origin as { lat: number; lng: number } | null | undefined;
                await recalcularRotaPlanejada({
                    rotaId: rota.id,
                    pontos: minhas.map(tarefaParaPontoRot),
                    origin: snapOrigin || undefined,
                    geradaEmLida: (rota as any).gerada_em ?? null,
                });
                await fetchRotasAtivas(vendedoresEfetivos);
            } finally {
                sincronizandoRef.current = false;
            }
        } else {
            // Em execução: não desmonta a rota sozinho — só avisa e oferece o recálculo.
            setAvisoNovaVisita(novas.length > 0 ? { rotaId: rota.id, qtd: novas.length } : null);
        }
    };

    useEffect(() => {
        if (profile && !loading) sincronizarRotaDoDia();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tarefas, rotasAtivas, dataSel]);

    const recalcularComNovas = async () => {
        if (!avisoNovaVisita || !profile) return;
        const minhas = tarefas.filter(t => tarefaRoteirizavel(t) && String(t.responsavel_id) === String(profile.id));
        try {
            await anexarERecalcularPendentes({
                rotaId: avisoNovaVisita.rotaId,
                novosPontos: minhas.map(tarefaParaPontoRot),
                origin: await getOrigin(),
            });
            addToast('Rota recalculada com as novas visitas 🚗', 'success');
            setAvisoNovaVisita(null);
            await fetchRotasAtivas(vendedoresEfetivos);
        } catch (err) {
            console.error('Erro ao recalcular rota:', err);
            addToast('Erro ao recalcular a rota.', 'error');
        }
    };

    // Realtime: visitas lançadas de qualquer lugar do CRM atualizam a agenda sem refresh manual.
    useAgendaRealtime({
        enabled: !!profile && !loading,
        onTarefasChange: () => { if (profile) refreshAll(vendedoresEfetivos); },
        onRotasChange: () => { if (profile) fetchRotasAtivas(vendedoresEfetivos); },
    });

    // Selecionar uma data de outro mês pelo calendário sincroniza o mês exibido.
    // Selecionar um dia é sempre uma interação de "Dia" — a barra de indicadores acompanha.
    const selecionarData = (iso: string) => {
        setDataSel(iso);
        setKpiScope('dia');
        const d = new Date(iso + 'T12:00:00');
        if (d.getMonth() !== mesAtual.getMonth() || d.getFullYear() !== mesAtual.getFullYear()) {
            setMesAtual(new Date(d.getFullYear(), d.getMonth(), 1));
        }
    };

    // Resumo por dia p/ o calendário (visitas, retornos, total, kWh potencial)
    const resumoPorDia = useMemo(() => {
        const map: Record<string, DiaResumo> = {};
        for (const t of tarefasMes) {
            const key = (t.vencimento || '').slice(0, 10);
            if (!key) continue;
            if (!map[key]) map[key] = { total: 0, visitas: 0, retornos: 0, kwh: 0, presenciais: 0, ligacoes: 0 };
            map[key].total++;
            // Tarefa sem "Tipo da Visita" (tipo NULL, ex.: criada direto no Kanban) conta
            // como visita comum — toda atividade agendada entra na régua.
            if (t.tipo === 'retorno') map[key].retornos++;
            else map[key].visitas++;
            // Modalidade da visita (Bruno): presencial entra na rota; remota = "Remotas do dia"
            if (isRemota(t.modalidade)) map[key].ligacoes++;
            else map[key].presenciais++;
            map[key].kwh += Number(t.negociacoes?.consumo_medio_kwh || 0);
        }
        return map;
    }, [tarefasMes]);

    const kwhDoDia = useMemo(
        () => tarefas.reduce((s, t) => s + Number(t.negociacoes?.consumo_medio_kwh || 0), 0),
        [tarefas]
    );

    const getOrigin = (): Promise<{ lat: number; lng: number } | undefined> =>
        new Promise(resolve => {
            if (!navigator.geolocation) return resolve(undefined);
            navigator.geolocation.getCurrentPosition(
                pos => resolve({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
                () => resolve(undefined),
                { timeout: 4000 }
            );
        });

    // Re-ancora as paradas pendentes da rota no GPS atual (meio do dia, fora da ordem planejada).
    const reordenarRotaDaqui = async (rota: RotaAtiva) => {
        try {
            const origin = await getOrigin();
            if (!origin) { addToast('Não consegui obter sua localização — ative o GPS e tente de novo.', 'warning'); return; }
            const n = await reordenarPendentesAPartirDe(rota.id, origin);
            addToast(n > 0 ? `Rota reordenada a partir da sua posição — ${n} paradas pendentes. 📍` : 'Nada para reordenar.', n > 0 ? 'success' : 'info');
            await fetchRotasAtivas(vendedoresEfetivos);
        } catch (err) {
            console.error('Erro ao reordenar rota:', err);
            addToast('Erro ao reordenar a rota.', 'error');
        }
    };

    // ── Rota Inteligente: geração a partir das visitas planejadas do dia (uma rota por responsável) ──
    // Ponto de geração carrega o responsável, para agrupar e montar uma rota por vendedor.
    interface PontoGer {
        id: string;
        lat: number;
        lng: number;
        label: string;
        negociacao_id: number;
        tarefa_id?: number;
        kwh?: number | null;
        cidade?: string | null;
        bairro?: string | null;
        responsavel_id: string;
    }

    // Visita remota não entra no cálculo de km/rota (doc 03 §1) — só presenciais geolocalizadas são roteirizáveis.
    // Visitas já concluídas não são re-roteadas.
    const tarefaRoteirizavel = (t: TarefaDia) => !!(t.negociacoes?.latitude && t.negociacoes?.longitude && t.negociacao_id && isPresencial(t.modalidade) && t.status !== 'concluido');

    const tarefaParaPonto = (t: TarefaDia): PontoGer => ({
        id: String(t.id),
        lat: t.negociacoes!.latitude!,
        lng: t.negociacoes!.longitude!,
        label: t.negociacoes!.titulo || t.titulo,
        negociacao_id: t.negociacao_id!,
        tarefa_id: t.id,
        kwh: t.negociacoes?.consumo_medio_kwh ?? null,
        cidade: t.negociacoes?.cidade ?? null,
        bairro: t.negociacoes?.bairro ?? null,
        responsavel_id: String(t.responsavel_id || vendedorId),
    });

    // Agrupa pontos por responsável (uma rota por vendedor)
    const agruparPorVendedor = (pontos: PontoGer[]): Record<string, PontoGer[]> => {
        const grupos: Record<string, PontoGer[]> = {};
        for (const p of pontos) {
            const k = p.responsavel_id || vendedorId;
            (grupos[k] ||= []).push(p);
        }
        return grupos;
    };

    const pontoGerParaRoteirizavel = (p: PontoGer): PontoRoteirizavel => ({
        id: p.id, lat: p.lat, lng: p.lng, label: p.label, negociacao_id: p.negociacao_id, tarefa_id: p.tarefa_id!,
    });

    // Botão único "Criar Rota Inteligente": GPS do próprio usuário → TSP → rota pronta para execução.
    // Uma rota por responsável (spec §3.3). O GPS só ancora a rota do próprio usuário; para as
    // visitas de outro responsável (gestor), a rota parte da 1ª parada.
    const criarRotaInteligente = async (pontos: PontoGer[]) => {
        const roteaveis = pontos.filter(p => p.tarefa_id);
        if (roteaveis.length === 0) {
            addToast('Nenhuma parada com localização para gerar a rota.', 'error');
            return;
        }
        setGerando(true);
        try {
            const grupos = agruparPorVendedor(roteaveis);
            const meuOrigin = await getOrigin(); // GPS só vale p/ as minhas visitas
            let criadas = 0;
            let ultimaId: string | null = null;
            for (const [respId, pts] of Object.entries(grupos)) {
                if (pts.length === 0) continue;
                const origin = String(respId) === String(profile?.id) ? meuOrigin : undefined;
                const r = await gerarRotaInteligente({
                    responsavelId: respId,
                    data: dataSel,
                    pontos: pts.map(pontoGerParaRoteirizavel),
                    origin,
                });
                ultimaId = r.rotaId;
                criadas++;
            }
            addToast(
                criadas === 1 ? 'Rota Inteligente pronta! 🚗 Confira as paradas e inicie.' : `${criadas} rotas inteligentes criadas — uma por responsável 🚗`,
                'success'
            );
            logUsageEvent({ userId: profile?.id, modulo: 'crm', funcionalidade: 'rotas', acao: 'criar_rota_inteligente', origem: 'acao' });
            setRotaDestaqueId(ultimaId);
            setSelecionados([]);
            setTarefasSel([]);
            await Promise.all([fetchRotasAtivas(vendedoresEfetivos), fetchTarefas(vendedoresEfetivos)]);
        } catch (err) {
            console.error('Erro ao criar rota inteligente:', err);
            addToast('Erro ao criar a rota inteligente.', 'error');
        } finally {
            setGerando(false);
        }
    };

    const criarRotaInteligenteDoDia = () => criarRotaInteligente(tarefas.filter(tarefaRoteirizavel).map(tarefaParaPonto));
    const criarRotaInteligenteSelecao = () => criarRotaInteligente(tarefasSelObjs.filter(tarefaRoteirizavel).map(tarefaParaPonto));

    // "Ver todos os leads": agendar visitas (tarefas) na data selecionada — a rota nasce depois,
    // no dia, pelo botão inteligente. Mantém a visita planejada como fonte única de verdade.
    const agendarVisitasSelecionadas = async () => {
        const alvo = leads.filter(l => selecionados.includes(l.id));
        if (alvo.length === 0) return;
        setGerando(true);
        try {
            const agora = new Date().toISOString();
            const rows = alvo.map(l => ({
                titulo: `Visita: ${l.titulo}`,
                responsavel_id: String((l as any).responsavel_id || vendedorId),
                solicitante_id: profile?.id,
                prioridade: 'media',
                vencimento: dataSel,
                status: 'a_iniciar',
                negociacao_id: l.id,
                tipo: 'visita',
                modalidade: 'presencial',
                created_at: agora,
            }));
            const { error } = await supabase.from('tarefas').insert(rows);
            if (error) throw error;
            addToast(`${alvo.length} visita(s) agendada(s) para ${fmtData(dataSel)} 📅`, 'success');
            logUsageEvent({ userId: profile?.id, modulo: 'crm', funcionalidade: 'rotas', acao: 'agendar_visitas_selecionadas', origem: 'acao' });
            setSelecionados([]);
            await Promise.all([fetchTarefas(vendedoresEfetivos), fetchTarefasMes(vendedoresEfetivos, mesAtual), fetchLeads()]);
        } catch (err) {
            console.error('Erro ao agendar visitas:', err);
            addToast('Erro ao agendar as visitas.', 'error');
        } finally {
            setGerando(false);
        }
    };

    // Registra a alteração no histórico do lead (auditoria) — uma nota por negociação
    const registrarAuditoria = async (alvos: TarefaDia[], conteudo: (t: TarefaDia) => string) => {
        if (!profile) return;
        const notas = alvos
            .filter(t => t.negociacao_id)
            .map(t => ({ negociacao_id: t.negociacao_id!, user_id: profile.id, conteudo: conteudo(t), tipo: 'nota' as const }));
        if (notas.length === 0) return;
        const { error } = await supabase.from('negociacao_notas').insert(notas);
        if (error) console.error('Erro ao registrar auditoria:', error);
    };

    const fmtData = (iso: string) => new Date(iso + 'T12:00:00').toLocaleDateString('pt-BR');

    const alterarDataSelecionadas = async (novaData: string) => {
        if (tarefasSel.length === 0) return;
        setBulkSalvando(true);
        try {
            const { error } = await supabase.from('tarefas').update({ vencimento: novaData }).in('id', tarefasSel);
            if (error) throw error;
            await registrarAuditoria(tarefasSelObjs, () => `📅 Visita reagendada: ${fmtData(dataSel)} → ${fmtData(novaData)}`);
            if (profile) {
                await registrarRemanejamentos(tarefasSelObjs.map(t => ({
                    rota_origem_id: paradasDia.get(t.id)?.rota_id ?? null,
                    parada_id: paradasDia.get(t.id)?.id ?? null,
                    tarefa_id: t.id,
                    negociacao_id: t.negociacao_id ?? null,
                    user_id: profile.id,
                    data_original: dataSel,
                    nova_data: novaData,
                    origem: 'gestor' as const,
                })));
            }
            addToast(`${tarefasSel.length} visita(s) movida(s) para ${fmtData(novaData)} 📅`, 'success');
            logUsageEvent({ userId: profile?.id, modulo: 'crm', funcionalidade: 'rotas', acao: 'reagendar_tarefas_selecionadas', origem: 'acao' });
            setBulkAcao(null);
            setTarefasSel([]);
            await Promise.all([fetchTarefas(vendedoresEfetivos), fetchTarefasMes(vendedoresEfetivos, mesAtual)]);
        } catch (err) {
            console.error('Erro ao alterar data:', err);
            addToast('Erro ao alterar a data das visitas.', 'error');
        } finally {
            setBulkSalvando(false);
        }
    };

    // Reagendamento inline de UMA visita direto do calendário (doc 03 §5):
    // move a tarefa de dia, tira-a da rota de origem e — se existir rota no destino — anexa lá.
    // Não cria rota nova; o calendário re-conta sozinho (conta por vencimento).
    const reagendarVisitaInline = async (tarefa: TarefaDia, novaData: string) => {
        if (novaData === dataSel) { setReagendarVisita(null); return; }
        setReagendandoInline(true);
        try {
            // 1) move a visita para a nova data (reativa se estava aguardando)
            const novoStatus = tarefa.status === 'aguardando' ? 'a_iniciar' : tarefa.status;
            const { error: upErr } = await supabase.from('tarefas')
                .update({ vencimento: novaData, status: novoStatus }).eq('id', tarefa.id);
            if (upErr) throw upErr;

            // 2) sai da rota do dia de origem (se fazia parte de uma)
            const paradaOrigem = paradasDia.get(tarefa.id);
            if (paradaOrigem) {
                await supabase.from('rota_paradas').delete().eq('id', paradaOrigem.id);
            }

            // 3) se já existe rota (não concluída/cancelada) do mesmo responsável no destino, anexa a parada
            if (tarefa.negociacao_id) {
                const { data: rotaDestino } = await supabase
                    .from('rotas_comerciais')
                    .select('id, rota_paradas(id)')
                    .eq('vendedor_id', tarefa.responsavel_id || vendedorId)
                    .eq('data', novaData)
                    .in('status', ['planejada', 'agendada', 'reagendada', 'em_execucao'])
                    .order('created_at', { ascending: false })
                    .limit(1)
                    .maybeSingle();
                if (rotaDestino) {
                    const proxPos = ((rotaDestino as any).rota_paradas?.length || 0) + 1;
                    await supabase.from('rota_paradas').insert([{
                        rota_id: (rotaDestino as any).id,
                        posicao: proxPos,
                        negociacao_id: tarefa.negociacao_id,
                        tarefa_id: tarefa.id,
                        modalidade: normalizarModalidade(tarefa.modalidade),
                    }]);
                }
            }

            await registrarAuditoria([tarefa], () => `📅 Visita reagendada: ${fmtData(dataSel)} → ${fmtData(novaData)}`);
            if (profile) {
                await registrarRemanejamentos([{
                    rota_origem_id: paradaOrigem?.rota_id ?? null,
                    parada_id: paradaOrigem?.id ?? null,
                    tarefa_id: tarefa.id,
                    negociacao_id: tarefa.negociacao_id ?? null,
                    user_id: profile.id,
                    data_original: dataSel,
                    nova_data: novaData,
                    origem: 'gestor' as const,
                }]);
            }
            addToast(`Visita movida para ${fmtData(novaData)} 📅`, 'success');
            logUsageEvent({ userId: profile?.id, modulo: 'crm', funcionalidade: 'rotas', acao: 'reagendar_visita', origem: 'acao' });
            setReagendarVisita(null);
            await Promise.all([
                fetchTarefas(vendedoresEfetivos),
                fetchTarefasMes(vendedoresEfetivos, mesAtual),
                fetchRotasAtivas(vendedoresEfetivos),
            ]);
        } catch (err) {
            console.error('Erro ao reagendar visita:', err);
            addToast('Erro ao reagendar a visita.', 'error');
        } finally {
            setReagendandoInline(false);
        }
    };

    const atribuirVendedorSelecionadas = async (novoId: string) => {
        if (!novoId || tarefasSel.length === 0) return;
        setBulkSalvando(true);
        try {
            const nome = profiles.find(p => String(p.id) === String(novoId))?.full_name || 'outro responsável';
            const { error } = await supabase.from('tarefas').update({ responsavel_id: novoId }).in('id', tarefasSel);
            if (error) throw error;
            await registrarAuditoria(tarefasSelObjs, () => `👤 Visita atribuída a ${nome}`);
            addToast(`${tarefasSel.length} visita(s) atribuída(s) a ${nome} 👤`, 'success');
            logUsageEvent({ userId: profile?.id, modulo: 'crm', funcionalidade: 'rotas', acao: 'atribuir_vendedor_selecionadas', origem: 'acao' });
            setBulkAcao(null);
            setTarefasSel([]);
            await Promise.all([fetchTarefas(vendedoresEfetivos), fetchTarefasMes(vendedoresEfetivos, mesAtual)]);
        } catch (err) {
            console.error('Erro ao atribuir vendedor:', err);
            addToast('Erro ao atribuir as visitas.', 'error');
        } finally {
            setBulkSalvando(false);
        }
    };

    const removerDaRotaSelecionadas = async () => {
        if (tarefasSel.length === 0) return;
        setBulkSalvando(true);
        try {
            const { error } = await supabase.from('tarefas').update({ status: 'cancelado' }).in('id', tarefasSel);
            if (error) throw error;
            await registrarAuditoria(tarefasSelObjs, () => `🗑️ Visita removida do roteiro de ${fmtData(dataSel)}`);
            addToast(`${tarefasSel.length} visita(s) removida(s) do roteiro.`, 'info');
            logUsageEvent({ userId: profile?.id, modulo: 'crm', funcionalidade: 'rotas', acao: 'remover_da_rota_selecionadas', origem: 'acao' });
            setBulkAcao(null);
            setTarefasSel([]);
            await Promise.all([fetchTarefas(vendedoresEfetivos), fetchTarefasMes(vendedoresEfetivos, mesAtual)]);
        } catch (err) {
            console.error('Erro ao remover do roteiro:', err);
            addToast('Erro ao remover as visitas.', 'error');
        } finally {
            setBulkSalvando(false);
        }
    };

    const iniciarRota = async (rota: RotaAtiva) => {
        const { error } = await supabase.from('rotas_comerciais')
            .update({ status: 'em_execucao', iniciada_em: new Date().toISOString() })
            .eq('id', rota.id);
        if (error) { addToast('Erro ao iniciar rota.', 'error'); return; }
        addToast(`Rota iniciada com ${rota.rota_paradas.length} visitas! 🚗`, 'success');
        logUsageEvent({ userId: profile?.id, modulo: 'crm', funcionalidade: 'rotas', acao: 'iniciar_rota', origem: 'acao' });
        await fetchRotasAtivas(vendedoresEfetivos);
    };

    // Check-in com foto (motoboy/leitura de medidor) -> parada vira 'em_visita'
    const iniciarVisita = async (info: VisitaParadaInfo, foto: File, rota: RotaAtiva) => {
        if (!profile) return;
        try {
            const geo = await getOrigin();
            const ext = (foto.name.split('.').pop() || 'jpg').toLowerCase();
            const path = `${rota.id}/${info.id}_${Date.now()}.${ext}`;
            const { error: upErr } = await supabase.storage.from('visitas').upload(path, foto);
            if (upErr) throw upErr;
            const { data: { publicUrl } } = supabase.storage.from('visitas').getPublicUrl(path);

            const { error } = await supabase.from('rota_paradas')
                .update({
                    status: 'em_visita', checkin_em: new Date().toISOString(), foto_url: publicUrl,
                    checkin_lat: geo?.lat ?? null, checkin_lng: geo?.lng ?? null,
                })
                .eq('id', info.id);
            if (error) throw error;

            setVisitaModal(null);
            addToast('Check-in realizado! Boa visita 🤝', 'success');
            logUsageEvent({ userId: profile?.id, modulo: 'crm', funcionalidade: 'rotas', acao: 'checkin_visita_foto', origem: 'acao' });
            await fetchRotasAtivas(vendedoresEfetivos);
        } catch (err) {
            console.error('Erro no check-in:', err);
            addToast('Erro ao fazer check-in. Tente novamente.', 'error');
        }
    };

    // Check-in comercial (vendedor): instantâneo, sem foto — só geolocalização automática
    const iniciarVisitaComercial = async (parada: Parada) => {
        if (!profile) return;
        try {
            const geo = await getOrigin();
            const { error } = await supabase.from('rota_paradas')
                .update({
                    status: 'em_visita', checkin_em: new Date().toISOString(),
                    checkin_lat: geo?.lat ?? null, checkin_lng: geo?.lng ?? null,
                })
                .eq('id', parada.id);
            if (error) throw error;
            addToast(`Visita iniciada em ${parada.negociacoes?.titulo || 'lead'} 🤝`, 'success');
            logUsageEvent({ userId: profile?.id, modulo: 'crm', funcionalidade: 'rotas', acao: 'checkin_visita_comercial', origem: 'acao' });
            await fetchRotasAtivas(vendedoresEfetivos);
        } catch (err) {
            console.error('Erro no check-in:', err);
            addToast('Erro ao iniciar a visita.', 'error');
        }
    };

    // Check-out: comercial = status da visita + observação/próxima ação; foto = observação opcional.
    // Sempre: geolocalização, tempo de permanência, conclui tarefa e registra no lead.
    const encerrarVisita = async (info: VisitaParadaInfo, dados: VisitaConclusao, rota: RotaAtiva) => {
        if (!profile) return;
        const parada = rota.rota_paradas.find(p => p.id === info.id);
        try {
            const geo = await getOrigin();
            const agora = new Date().toISOString();
            const { error } = await supabase.from('rota_paradas')
                .update({
                    status: 'visitado', checkout_em: agora, visitado_em: agora,
                    descricao_visita: dados.descricao || null,
                    status_visita: dados.statusVisita || null,
                    proxima_acao: dados.proximaAcao || null,
                    checkout_lat: geo?.lat ?? null, checkout_lng: geo?.lng ?? null,
                })
                .eq('id', info.id);
            if (error) throw error;
            logUsageEvent({ userId: profile?.id, modulo: 'crm', funcionalidade: 'rotas', acao: 'encerrar_visita', origem: 'acao' });

            if (parada?.tarefa_id) {
                // Resultado (status + observação) fica na descrição → aparece no card da agenda
                await concluirTarefaComResultado(parada.tarefa_id, textoResultado(dados), dados.resultadoCategoria);
            }
            if (parada?.negociacao_id) {
                const inicio = parada.checkin_em ? new Date(parada.checkin_em).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' }) : '?';
                const fim = new Date().toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
                const permanencia = parada.checkin_em
                    ? ` · ${Math.max(1, Math.round((Date.now() - new Date(parada.checkin_em).getTime()) / 60000))} min no local`
                    : '';
                const partes = [
                    dados.statusVisita ? `📋 ${statusVisitaLabel(dados.statusVisita)}` : '✅ Visita realizada',
                    `(${new Date().toLocaleDateString('pt-BR')} · ${inicio} → ${fim}${permanencia})`,
                ];
                if (dados.descricao) partes.push(`— ${dados.descricao}`);
                if (dados.proximaAcao) partes.push(`· Próxima ação: ${proximaAcaoLabel(dados.proximaAcao)}`);
                await supabase.from('negociacao_notas').insert([{
                    negociacao_id: parada.negociacao_id,
                    user_id: profile.id,
                    conteudo: partes.join(' '),
                    tipo: 'nota',
                }]);

                // Visita concluída conta como interação (doc 01 §3.6): zera "Sem interação"/"Esfriando" do lead
                await supabase.from('negociacoes')
                    .update({ ultima_atividade_at: agora, updated_at: agora })
                    .eq('id', parada.negociacao_id);

                // Loop "concluir → planejar próxima" (doc 03 §4): cria a próxima visita numa única ação
                if (dados.proximaVisitaData) {
                    await supabase.from('tarefas').insert([{
                        titulo: `Visita: ${parada.negociacoes?.titulo || 'lead'}`,
                        responsavel_id: rota.vendedor_id || profile.id,
                        solicitante_id: profile.id,
                        prioridade: 'media',
                        vencimento: dados.proximaVisitaData,
                        status: 'a_iniciar',
                        negociacao_id: parada.negociacao_id,
                        tipo: 'visita',
                        modalidade: dados.proximaVisitaModalidade || 'presencial',
                        created_at: new Date().toISOString(),
                    }]);
                }
            }
            setVisitaModal(null);

            // Avança para o próximo lead da rota — com atalho de navegação de destino
            // único (sem o limite de 10 paradas dos links de rota do Google Maps)
            const proxima = rota.rota_paradas
                .filter(p => p.id !== info.id && p.status === 'pendente')
                .sort((a, b) => a.posicao - b.posicao)[0];
            addToast(
                proxima
                    ? `Visita concluída! ✅ Próximo: ${proxima.negociacoes?.titulo || `Parada ${proxima.posicao}`}`
                    : 'Visita concluída! ✅ Essa foi a última parada da rota 🎉',
                'success',
                proxima ? 8000 : 4000,
                urlNavegacao(proxima?.negociacoes)
                    ? {
                        label: '🧭 Navegar',
                        onClick: () => window.open(urlNavegacao(proxima!.negociacoes), '_blank', 'noopener'),
                    }
                    : undefined
            );
            await Promise.all([fetchRotasAtivas(vendedoresEfetivos), fetchTarefas(vendedoresEfetivos)]);
        } catch (err) {
            console.error('Erro no check-out:', err);
            addToast('Erro ao concluir a visita.', 'error');
        }
    };

    // Conclui a rota; se vier novaData, reagenda as visitas pendentes para essa data (doc 03 §5)
    const finalizarRota = async (rota: RotaAtiva, pendentes: Parada[], novaData?: string) => {
        if (novaData && pendentes.length) {
            setBulkSalvando(true);
            try {
                const comTarefa = pendentes.filter(p => p.tarefa_id);
                const semTarefa = pendentes.filter(p => !p.tarefa_id && p.negociacao_id);
                if (comTarefa.length) {
                    await supabase.from('tarefas')
                        .update({ vencimento: novaData, status: 'a_iniciar' })
                        .in('id', comTarefa.map(p => p.tarefa_id as number));
                }
                if (semTarefa.length) {
                    await supabase.from('tarefas').insert(semTarefa.map(p => ({
                        titulo: `Visita: ${p.negociacoes?.titulo || 'lead'}`,
                        responsavel_id: rota.vendedor_id || profile?.id,
                        solicitante_id: profile?.id,
                        prioridade: 'media',
                        vencimento: novaData,
                        status: 'a_iniciar',
                        negociacao_id: p.negociacao_id,
                        tipo: 'visita',
                        modalidade: normalizarModalidade(p.modalidade),
                        created_at: new Date().toISOString(),
                    })));
                }
            } catch (err) {
                console.error('Erro ao reagendar visitas:', err);
                addToast('Erro ao reagendar as visitas pendentes.', 'error');
                setBulkSalvando(false);
                return;
            }
            setBulkSalvando(false);
        }
        const visitadas = rota.rota_paradas.filter(p => p.status === 'visitado').length;
        const total = rota.rota_paradas.length;
        const { error } = await supabase.from('rotas_comerciais')
            .update({ status: 'concluida', concluida_em: new Date().toISOString() })
            .eq('id', rota.id);
        if (error) { addToast('Erro ao concluir rota.', 'error'); return; }
        logUsageEvent({ userId: profile?.id, modulo: 'crm', funcionalidade: 'rotas', acao: 'finalizar_rota', origem: 'acao' });
        // Resumo rápido de fim de rota (adendo §3): "14/17 visitadas, 3 remarcadas".
        addToast(
            novaData && pendentes.length
                ? `Rota concluída! ${visitadas}/${total} visitadas · ${pendentes.length} remarcada(s) p/ ${fmtData(novaData)} 🎉`
                : `Rota concluída! ${visitadas}/${total} visitadas 🎉`,
            'success'
        );
        setReagendarRota(null);
        await Promise.all([fetchRotasAtivas(vendedoresEfetivos), fetchTarefas(vendedoresEfetivos), fetchTarefasMes(vendedoresEfetivos, mesAtual)]);
    };

    const concluirRota = (rota: RotaAtiva) => {
        const pendentes = rota.rota_paradas.filter(p => p.status !== 'visitado' && p.status !== 'pulado');
        if (pendentes.length > 0) {
            const amanha = new Date(); amanha.setDate(amanha.getDate() + 1);
            setReagendarData(`${amanha.getFullYear()}-${String(amanha.getMonth() + 1).padStart(2, '0')}-${String(amanha.getDate()).padStart(2, '0')}`);
            setReagendarRota({ rota, pendentes });
            return;
        }
        finalizarRota(rota, []);
    };

    const cancelarRota = async (rota: RotaAtiva) => {
        const { error } = await supabase.from('rotas_comerciais')
            .update({ status: 'cancelada' })
            .eq('id', rota.id);
        if (error) { addToast('Erro ao cancelar rota.', 'error'); return; }
        addToast('Rota cancelada.', 'info');
        logUsageEvent({ userId: profile?.id, modulo: 'crm', funcionalidade: 'rotas', acao: 'cancelar_rota', origem: 'acao' });
        await fetchRotasAtivas(vendedoresEfetivos);
    };

    // Observações da rota (spec §3.1): campo opcional editável DEPOIS de a rota já existir.
    const salvarObservacoesRota = async (rota: RotaAtiva, texto: string) => {
        const { error } = await supabase.from('rotas_comerciais').update({ observacoes: texto.trim() || null }).eq('id', rota.id);
        if (error) { addToast('Erro ao salvar observações.', 'error'); return; }
        addToast('Observações salvas.', 'success');
        logUsageEvent({ userId: profile?.id, modulo: 'crm', funcionalidade: 'rotas', acao: 'salvar_observacoes_rota', origem: 'acao' });
        await fetchRotasAtivas(vendedoresEfetivos);
    };

    // Reordenação manual pós-otimização (spec §5): troca 2 paradas e recalcula km local.
    const reordenarParada = async (rota: RotaAtiva, paradaId: string, dir: -1 | 1) => {
        const ordenadas = [...rota.rota_paradas].sort((a, b) => a.posicao - b.posicao);
        const idx = ordenadas.findIndex(p => p.id === paradaId);
        const alvo = idx + dir;
        if (idx < 0 || alvo < 0 || alvo >= ordenadas.length) return;
        const a = ordenadas[idx], b = ordenadas[alvo];
        try {
            await supabase.from('rota_paradas').update({ posicao: b.posicao }).eq('id', a.id);
            await supabase.from('rota_paradas').update({ posicao: a.posicao }).eq('id', b.id);
            logUsageEvent({ userId: profile?.id, modulo: 'crm', funcionalidade: 'rotas', acao: 'reordenar_parada', origem: 'acao' });
            const nova = [...ordenadas]; nova[idx] = b; nova[alvo] = a;
            const pts = nova
                .map(p => (p.negociacoes?.latitude && p.negociacoes?.longitude) ? { lat: p.negociacoes.latitude, lng: p.negociacoes.longitude } : null)
                .filter(Boolean) as { lat: number; lng: number }[];
            let km = 0; for (let i = 0; i < pts.length - 1; i++) km += haversineKm(pts[i], pts[i + 1]);
            await supabase.from('rotas_comerciais').update({ distancia_total_km: Math.round(km * 10) / 10, duracao_min: null }).eq('id', rota.id);
            await fetchRotasAtivas(vendedoresEfetivos);
        } catch (err) {
            console.error('Erro ao reordenar parada:', err);
            addToast('Erro ao reordenar a parada.', 'error');
        }
    };

    const leadsFiltrados = useMemo(() => {
        const term = search.trim().toLowerCase();
        const min = parseFloat(consumoMin) || 0;
        return leads.filter(l => {
            if (min > 0 && (l.consumo_medio_kwh || 0) < min) return false;
            if (term) {
                const hay = `${l.titulo} ${l.cidade || ''} ${l.bairro || ''}`.toLowerCase();
                if (!hay.includes(term)) return false;
            }
            return true;
        });
    }, [leads, search, consumoMin]);

    const tarefasComLocal = tarefas.filter(tarefaRoteirizavel).length;
    const tarefasSemNegociacao = tarefas.filter(t => !t.negociacao_id).length;
    const tarefasSemLocal = tarefas.filter(t => t.negociacao_id && isPresencial(t.modalidade) && !t.negociacoes?.latitude).length;
    // Split do dia: visitas presenciais (entram na rota) × ligações (doc 03 §1 / §6)
    const visitasDia = tarefas.filter(t => isPresencial(t.modalidade));
    const ligacoesDoDia = tarefas.filter(t => isRemota(t.modalidade));

    // Rota do dia (contexto de 1 responsável): decide o botão do painel — criar × progresso (spec §2/§4D).
    // No modo multi (gestor c/ vários) o botão segue criando 1 rota por responsável.
    const rotaDoDia = useMemo(() => {
        if (multi) return null;
        return rotasAtivas.find(r => r.data === dataSel && String(r.vendedor_id) === String(vendedorId)) || null;
    }, [rotasAtivas, dataSel, vendedorId, multi]);
    const rotaDoDiaProgresso = useMemo(() => {
        if (!rotaDoDia) return null;
        const total = rotaDoDia.rota_paradas.length;
        const feitas = rotaDoDia.rota_paradas.filter(p => p.status === 'visitado').length;
        return { total, feitas };
    }, [rotaDoDia]);

    // Coluna da Agenda: rotas do dia selecionado + a que estiver em execução (nunca some)
    // primeiro; as demais rotas ativas aparecem logo abaixo em "Outras rotas ativas"
    // (aba "Rotas Ativas" foi unificada na Agenda — pedido do usuário, jul/2026).
    const rotasDoDiaSel = useMemo(
        () => rotasAtivas.filter(r => r.data === dataSel || r.status === 'em_execucao'),
        [rotasAtivas, dataSel]
    );
    const outrasRotasAtivas = useMemo(
        () => rotasAtivas.filter(r => !rotasDoDiaSel.some(d => d.id === r.id)),
        [rotasAtivas, rotasDoDiaSel]
    );

    // ── Barra de indicadores do gestor (spec §4A) ──
    // Dia = agrega `tarefas` (exato p/ dataSel); Semana = agrega `tarefasMes` no range da semana
    // efetivamente exibida no grid (semanaExibida, via onWeekChange — não deriva de dataSel: paginar
    // a semana no grid não seleciona um dia); Mês = agrega `tarefasMes` inteiro (já filtrado por mesAtual).
    const rangeSemana = (weekStartIso: string) => {
        const seg = new Date(weekStartIso + 'T12:00:00');
        const dom = new Date(seg); dom.setDate(seg.getDate() + 6);
        const iso = (x: Date) => `${x.getFullYear()}-${String(x.getMonth() + 1).padStart(2, '0')}-${String(x.getDate()).padStart(2, '0')}`;
        return { ini: iso(seg), fim: iso(dom) };
    };
    const [semanaExibida, setSemanaExibida] = useState(() => rangeSemana(hoje()));
    const kpi = useMemo(() => {
        // Toda atividade agendada conta nos totais de planejadas/concluídas,
        // independente da origem/tipo (Prospecção Geo, Kanban sem "Tipo da Visita", ...)
        // — mesma régua da Produtividade e do Dashboard de Tarefas. Só o kWh potencial
        // de ROTA continua excluindo ligações (não geram deslocamento).
        let planejadas = 0, concluidas = 0, kwhPot = 0, kwhReal = 0;
        const acumula = (mod: string | null | undefined, status: string, kwh: number) => {
            planejadas++;
            if (status === 'concluido') concluidas++;
            if (isRemota(mod)) return;
            kwhPot += kwh;
            if (status === 'concluido') kwhReal += kwh;
        };
        if (kpiScope === 'dia') {
            for (const t of tarefas) acumula(t.modalidade, t.status, Number(t.negociacoes?.consumo_medio_kwh || 0));
        } else if (kpiScope === 'semana') {
            for (const t of tarefasMes) {
                const dia = (t.vencimento || '').slice(0, 10);
                if (dia < semanaExibida.ini || dia > semanaExibida.fim) continue;
                acumula(t.modalidade, t.status, Number(t.negociacoes?.consumo_medio_kwh || 0));
            }
        } else {
            for (const t of tarefasMes) acumula(t.modalidade, t.status, Number(t.negociacoes?.consumo_medio_kwh || 0));
        }
        const pct = planejadas > 0 ? Math.round((concluidas / planejadas) * 100) : 0;
        return { planejadas, concluidas, pct, kwhPot, kwhReal };
    }, [kpiScope, tarefas, tarefasMes, semanaExibida]);

    // ── Meta diária: 12 visitas concluídas (adendo Bruno §4) ──
    // Contagem regressiva do PRÓPRIO dia de hoje: presenciais concluídas × meta.
    // Toda conclusão conta, independente da origem (Prospecção Geo, Kanban, ...):
    // tarefa criada sem "Tipo da Visita" (tipo NULL) também entra — só ligação fica fora.
    const metaHoje = useMemo(() => {
        if (dataSel !== hoje() || multi) return null;
        const presenciais = tarefas.filter(t => isPresencial(t.modalidade));
        const concluidas = presenciais.filter(t => t.status === 'concluido').length;
        return {
            concluidas,
            planejadas: presenciais.length,
            faltam: Math.max(0, META_CONCLUIDAS_DIA - concluidas),
            aposCorte: new Date().getHours() >= HORA_CORTE_META,
        };
    }, [tarefas, dataSel, multi]);

    // Visão do gestor: quantos vendedores (com agenda hoje) ainda não bateram as 12 concluídas.
    const fetchMetaEquipe = async () => {
        if (!isManager) return;
        const vendedorIds = profiles
            .filter(p => ['vendedor', 'vendedor_parceiro', 'parceiro'].includes((p as any).role))
            .map(p => String(p.id));
        if (vendedorIds.length === 0) { setMetaEquipe(null); return; }
        // Sem filtro de tipo: conclusão criada direto no Kanban (tipo NULL) também conta,
        // mesma régua do metaHoje do vendedor.
        const { data, error } = await supabase
            .from('tarefas')
            .select('responsavel_id, status, modalidade')
            .eq('vencimento', hoje())
            .neq('status', 'cancelado')
            .in('responsavel_id', vendedorIds);
        if (error) { console.error(error); return; }
        const por: Record<string, { concl: number }> = {};
        for (const t of (data || []) as { responsavel_id: string; status: string; modalidade?: string | null }[]) {
            if (isRemota(t.modalidade)) continue; // ligação não conta p/ a meta de visitas
            const v = (por[String(t.responsavel_id)] ||= { concl: 0 });
            if (t.status === 'concluido') v.concl++;
        }
        const comAgenda = Object.values(por);
        setMetaEquipe({
            total: comAgenda.length,
            abaixo: comAgenda.filter(v => v.concl < META_CONCLUIDAS_DIA).length,
        });
    };

    useEffect(() => {
        if (isManager && profiles.length > 0) fetchMetaEquipe();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [profiles, reloadTick]);

    // Concluir da lista do dia SEMPRE abre o modal "Concluir Visita" (pedido Bruno 10/jul:
    // observação obrigatória — não existe mais conclusão em um toque).
    const tarefaParaInfo = (t: TarefaDia): VisitaParadaInfo => ({
        id: String(t.id),
        titulo: t.negociacoes?.titulo || t.titulo,
        checkin_em: null,
        cidade: t.negociacoes?.cidade ?? null,
        bairro: t.negociacoes?.bairro ?? null,
        telefone: t.negociacoes?.whatsapp ?? null,
        kwh: t.negociacoes?.consumo_medio_kwh ?? null,
        modalidade: normalizarModalidade(t.modalidade),
        negociacaoId: t.negociacao_id ?? null,
        // Habilita os chips de resultado quando é visita de pós-venda.
        tipo: t.tipo ?? null,
    });

    // Submit do modal p/ visita da lista do dia: conclui a tarefa + registra no lead;
    // se a visita já estava numa rota, fecha a parada com os mesmos dados.
    const encerrarVisitaTarefa = async (t: TarefaDia, dados: VisitaConclusao) => {
        if (!profile) return;
        try {
            await concluirVisitaTarefa(
                { id: t.id, titulo: t.titulo, responsavel_id: t.responsavel_id, negociacao_id: t.negociacao_id, negociacao: { titulo: t.negociacoes?.titulo } },
                dados,
                profile.id
            );
            const parada = paradasDia.get(t.id);
            if (parada && parada.status !== 'visitado') {
                await supabase.from('rota_paradas')
                    .update({
                        status: 'visitado', visitado_em: new Date().toISOString(),
                        descricao_visita: dados.descricao || null,
                        status_visita: dados.statusVisita || null,
                        proxima_acao: dados.proximaAcao || null,
                    })
                    .eq('id', parada.id);
            }
            setVisitaTarefaModal(null);
            addToast(dados.proximaVisitaData ? 'Visita concluída e próxima agendada! ✅' : 'Visita concluída ✅', 'success');
            logUsageEvent({ userId: profile?.id, modulo: 'crm', funcionalidade: 'rotas', acao: 'encerrar_visita', origem: 'acao' });
            await refreshAll(vendedoresEfetivos); // atualiza dia, mês, rotas e a grade da Semana (reloadTick)
        } catch (err) {
            console.error('Erro ao concluir visita:', err);
            addToast('Erro ao concluir a visita.', 'error');
        }
    };

    // Altera o tipo (Presencial/Telefone) de uma visita já aberta — vale para as que já estavam em aberto
    const mudarModalidadeTarefa = async (tarefaId: number, nova: Modalidade) => {
        const { error } = await supabase.from('tarefas').update({ modalidade: nova }).eq('id', tarefaId);
        if (error) { addToast('Erro ao alterar o tipo da visita.', 'error'); return; }
        addToast(`Visita marcada como ${modalidadeLabel(nova)} ${modalidadeEmoji(nova)}.`, 'success');
        logUsageEvent({ userId: profile?.id, modulo: 'crm', funcionalidade: 'rotas', acao: 'mudar_modalidade_visita', origem: 'acao' });
        await Promise.all([fetchTarefas(vendedoresEfetivos), fetchTarefasMes(vendedoresEfetivos, mesAtual)]);
    };

    // Idem para uma parada de rota (sincroniza a tarefa de origem quando houver)
    const mudarModalidadeParada = async (paradaId: string, nova: Modalidade) => {
        const parada = rotasAtivas.flatMap(r => r.rota_paradas).find(p => p.id === paradaId);
        const { error } = await supabase.from('rota_paradas').update({ modalidade: nova }).eq('id', paradaId);
        if (error) { addToast('Erro ao alterar o tipo da visita.', 'error'); return; }
        if (parada?.tarefa_id) await supabase.from('tarefas').update({ modalidade: nova }).eq('id', parada.tarefa_id);
        logUsageEvent({ userId: profile?.id, modulo: 'crm', funcionalidade: 'rotas', acao: 'mudar_modalidade_visita', origem: 'acao' });
        await fetchRotasAtivas(vendedoresEfetivos);
    };

    // Linha de atividade do dia (compartilhada entre presenciais e "Remotas do dia")
    const renderTarefa = (t: TarefaDia, i: number) => {
        const roteirizavel = tarefaRoteirizavel(t);
        const checkedT = selSet.has(t.id);
        const isTel = isRemota(t.modalidade);
        const tel = (t.negociacoes?.whatsapp || '').replace(/\D/g, '');
        const sv = statusVisita(t);
        const jaVisitado = sv === 'visitado';
        const podeReagendar = !isMotoboy && (sv === 'pendente' || sv === 'roteirizada');
        return (
            <div
                key={t.id}
                onClick={!isMotoboy ? () => toggleSelTarefa(t.id) : undefined}
                className={`flex items-start gap-3 p-3 rounded-xl border transition-colors ${!isMotoboy ? 'cursor-pointer' : ''} ${checkedT
                    ? 'bg-primary/5 border-primary/40 ring-1 ring-primary/20'
                    : roteirizavel
                        ? 'bg-gray-50 dark:bg-gray-700/40 border-transparent hover:bg-gray-100 dark:hover:bg-gray-700/60'
                        : 'bg-gray-50/50 dark:bg-gray-700/20 border-transparent opacity-75 hover:opacity-100'}`}
            >
                {!isMotoboy && (
                    <input
                        type="checkbox"
                        checked={checkedT}
                        onChange={() => toggleSelTarefa(t.id)}
                        onClick={e => e.stopPropagation()}
                        className="mt-1 rounded text-primary focus:ring-primary shrink-0"
                    />
                )}
                <span className="w-6 h-6 rounded-full bg-primary text-white text-xs font-bold flex items-center justify-center shrink-0 mt-0.5">{i + 1}</span>
                <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                        {t.hora_visita && <span className="text-xs font-bold text-primary">{String(t.hora_visita).slice(0, 5)}</span>}
                        {t.negociacao_id ? (
                            <button onClick={e => { e.stopPropagation(); setLeadModalId(t.negociacao_id!); }} className={`text-sm font-semibold hover:text-primary text-left ${sv === 'visitado' ? 'text-gray-400 line-through' : 'text-gray-900 dark:text-gray-100'}`} title="Abrir lead">{t.titulo}</button>
                        ) : (
                            <p className={`text-sm font-semibold ${sv === 'visitado' ? 'text-gray-400 line-through' : 'text-gray-900 dark:text-gray-100'}`}>{t.titulo}</p>
                        )}
                        {/* Status de execução da visita (reflete o card do lead) */}
                        {sv === 'visitado' ? (
                            <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400 flex items-center gap-1"><CheckCircle2 size={9} /> Visitado</span>
                        ) : sv === 'em_visita' ? (
                            <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400 flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-blue-500 animate-pulse" /> Em visita</span>
                        ) : sv === 'roteirizada' ? (
                            <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-400 flex items-center gap-1"><Navigation size={9} /> Na rota</span>
                        ) : (
                            <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-gray-200 text-gray-500 dark:bg-gray-700 dark:text-gray-400">Não visitado</span>
                        )}
                        {/* Selo do tipo de visita (doc 03 §1) — clique alterna Presencial/Remota (vale p/ visitas já abertas) */}
                        <button
                            type="button"
                            onClick={e => { e.stopPropagation(); mudarModalidadeTarefa(t.id, isTel ? 'presencial' : 'remota'); }}
                            title="Clique para alternar Presencial / Telefone"
                            className={`text-[10px] font-bold px-2 py-0.5 rounded-full flex items-center gap-1 transition-all hover:ring-1 hover:ring-current/40 ${isTel ? 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400' : 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400'}`}
                        >
                            {isTel ? <Phone size={9} /> : <MapPin size={9} />} {isTel ? 'Telefone' : 'Presencial'}
                        </button>
                        {t.tipo && TIPO_VISITA_LABELS[t.tipo] && (
                            <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400">{TIPO_VISITA_LABELS[t.tipo]}</span>
                        )}
                    </div>
                    {t.objetivo && <p className="text-xs text-gray-600 dark:text-gray-300 mt-0.5">🎯 {t.objetivo}</p>}
                    {t.descricao && <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{t.descricao}</p>}
                    <div className="flex items-center gap-2 mt-1 flex-wrap">
                        <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${prioridadeBadge(t.prioridade)}`}>{t.prioridade === 'alta' ? '🔴 Alta' : t.prioridade === 'media' ? '🟡 Média' : '🟢 Baixa'}</span>
                        {t.negociacao_id ? (
                            <span className="text-[11px] text-gray-500 dark:text-gray-400 flex items-center gap-1">
                                {isTel ? <Phone size={10} className="text-purple-500" /> : roteirizavel ? <MapPin size={10} className="text-emerald-500" /> : <MapPinOff size={10} className="text-gray-400" />}
                                {t.negociacoes?.titulo}{isTel ? '' : roteirizavel ? ` · ${[t.negociacoes?.bairro, t.negociacoes?.cidade].filter(Boolean).join(', ')}` : ' (sem localização)'}
                            </span>
                        ) : (
                            <span className="text-[11px] text-gray-400 flex items-center gap-1"><MapPinOff size={10} /> sem negociação vinculada</span>
                        )}
                        {(t.negociacoes?.consumo_medio_kwh || 0) > 0 && (
                            <span className="text-[11px] font-semibold text-amber-600 dark:text-amber-400 flex items-center gap-0.5">
                                <Zap size={10} /> {Math.round(t.negociacoes!.consumo_medio_kwh!).toLocaleString('pt-BR')} kWh
                            </span>
                        )}
                        {isTel && tel && (
                            <span className="flex items-center gap-1" onClick={e => e.stopPropagation()}>
                                <a href={`tel:+55${tel}`} className="text-[11px] font-bold px-2 py-0.5 rounded-full bg-blue-50 text-blue-600 dark:bg-blue-900/20 dark:text-blue-400 flex items-center gap-1 hover:bg-blue-100 dark:hover:bg-blue-900/40"><Phone size={10} /> Ligar</a>
                                <a href={`https://wa.me/55${tel}`} target="_blank" rel="noopener noreferrer" className="text-[11px] font-bold px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-600 dark:bg-emerald-900/20 dark:text-emerald-400 flex items-center gap-1 hover:bg-emerald-100 dark:hover:bg-emerald-900/40"><MessageCircle size={10} /> WhatsApp</a>
                            </span>
                        )}
                    </div>
                </div>
                {!isMotoboy && (!jaVisitado || podeReagendar) && (
                    <div className="flex items-center gap-0.5 shrink-0 mt-0.5">
                        {!jaVisitado && (
                            <button
                                type="button"
                                onClick={e => { e.stopPropagation(); setVisitaTarefaModal(t); }}
                                title="Concluir visita — registra status e observação"
                                className="p-1.5 rounded-lg text-emerald-500 hover:text-emerald-600 hover:bg-emerald-50 dark:hover:bg-emerald-900/30 transition-colors"
                            >
                                <CheckCircle2 size={15} />
                            </button>
                        )}
                        {podeReagendar && (
                            <button
                                type="button"
                                onClick={e => { e.stopPropagation(); setReagendarVisita({ tarefa: t, anchor: e.currentTarget.getBoundingClientRect() }); }}
                                title="Reagendar esta visita"
                                className="p-1.5 rounded-lg text-gray-400 hover:text-primary hover:bg-primary/10 transition-colors"
                            >
                                <CalendarClock size={15} />
                            </button>
                        )}
                    </div>
                )}
            </div>
        );
    };

    // Lista plana "Ver todos os leads" (carteira completa): leads sem localização aparecem
    // marcados e não-roteáveis. Reutilizada no Roteiro Diário e na Roteirização Inteligente.
    const leadRoteavel = (l: MapaLead) => !!(l.latitude && l.longitude);
    const renderListaLeads = () => {
        const semLoc = leadsFiltrados.filter(l => !leadRoteavel(l)).length;
        const alvoSel = leadsFiltrados.filter(leadRoteavel).slice(0, 23).map(l => l.id);
        const todosMarcados = alvoSel.length > 0 && alvoSel.every(id => selecionados.includes(id));
        return (
            <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700 shadow-sm p-5">
                <div className="flex flex-wrap items-center gap-2 mb-4">
                    <div className="relative flex-1 min-w-[180px]">
                        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                        <input
                            value={search}
                            onChange={e => setSearch(e.target.value)}
                            placeholder="Buscar lead ou cidade..."
                            className="w-full pl-9 pr-3 py-2 text-sm rounded-xl bg-gray-50 dark:bg-gray-700/50 border border-gray-200 dark:border-gray-600 text-gray-800 dark:text-gray-100 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary/40"
                        />
                    </div>
                    <input
                        value={consumoMin}
                        onChange={e => setConsumoMin(e.target.value)}
                        placeholder="kWh mín."
                        type="number"
                        className="w-28 px-3 py-2 text-sm rounded-xl bg-gray-50 dark:bg-gray-700/50 border border-gray-200 dark:border-gray-600 text-gray-800 dark:text-gray-100 placeholder-gray-400 focus:outline-none"
                    />
                    <button
                        onClick={() => setSelecionados(todosMarcados ? [] : alvoSel)}
                        disabled={alvoSel.length === 0}
                        className="text-xs font-semibold text-primary hover:underline disabled:opacity-40 disabled:no-underline"
                    >
                        {todosMarcados ? 'Desmarcar todos' : 'Selecionar Todos'}
                    </button>
                    <button
                        onClick={agendarVisitasSelecionadas}
                        disabled={gerando || selecionados.length === 0}
                        title="Agenda as visitas no dia selecionado — a rota nasce no dia com um clique"
                        className="flex items-center gap-2 px-4 py-2 text-sm font-bold rounded-full bg-primary text-white shadow-sm hover:opacity-90 transition-opacity disabled:opacity-40 ml-auto"
                    >
                        <CalendarClock size={15} /> Agendar visitas ({selecionados.length})
                    </button>
                </div>

                <p className="text-xs text-gray-400 mb-2 flex flex-wrap items-center gap-1.5">
                    <span>Leads Disponíveis ({leadsFiltrados.length}){semLoc > 0 && <> · <span className="text-amber-500">{semLoc} sem localização</span></>} — carteira completa do funil. Máx. 23 por rota.</span>
                    {semLoc > 0 && (
                        <button
                            onClick={geocodificarPendentes}
                            disabled={geocodificando}
                            title="Busca as coordenadas dos leads que têm endereço/cidade mas ainda não têm localização (grátis, via OpenStreetMap)"
                            className="flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-bold bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-400 border border-amber-200 dark:border-amber-800 hover:bg-amber-100 disabled:opacity-50"
                        >
                            {geocodificando
                                ? <><Loader2 size={11} className="animate-spin" /> Localizando… {geoProgresso}</>
                                : <><MapPin size={11} /> Localizar pendentes</>}
                        </button>
                    )}
                </p>
                <div className="max-h-[420px] overflow-y-auto space-y-1.5 pr-1">
                    {leadsFiltrados.map(l => {
                        const routable = leadRoteavel(l);
                        const checked = selecionados.includes(l.id);
                        return (
                            <label
                                key={l.id}
                                className={`flex items-center gap-3 p-2.5 rounded-xl transition-colors border ${!routable
                                    ? 'bg-gray-50/40 dark:bg-gray-700/20 border-transparent opacity-70 cursor-default'
                                    : checked ? 'bg-primary/5 border-primary/30 cursor-pointer' : 'bg-gray-50/60 dark:bg-gray-700/30 border-transparent hover:bg-gray-100 dark:hover:bg-gray-700/60 cursor-pointer'}`}
                            >
                                <input
                                    type="checkbox"
                                    checked={checked}
                                    disabled={!routable}
                                    onChange={() => routable && setSelecionados(prev => checked ? prev.filter(id => id !== l.id) : (prev.length < 23 ? [...prev, l.id] : prev))}
                                    className="rounded text-primary focus:ring-primary disabled:opacity-40"
                                />
                                <div className="min-w-0 flex-1">
                                    <p className="text-sm font-semibold text-gray-900 dark:text-gray-100 truncate">{l.titulo}</p>
                                    <p className="text-[11px] text-gray-500 dark:text-gray-400">{[l.cidade, l.bairro].filter(Boolean).join(' · ') || '—'}</p>
                                </div>
                                {routable ? (
                                    <span className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: getLeadMapColor(l) }} title={getLeadMapLabel(l)}></span>
                                ) : (
                                    <span className="text-[9px] font-bold px-2 py-0.5 rounded-full bg-gray-200 text-gray-500 dark:bg-gray-700 dark:text-gray-400 shrink-0 flex items-center gap-1" title="Geolocalize no Mapa Inteligente para incluir na rota">
                                        <MapPinOff size={9} /> sem localização
                                    </span>
                                )}
                                <span className="flex items-center gap-1 text-[11px] font-semibold text-gray-600 dark:text-gray-300 shrink-0">
                                    <Zap size={11} className="text-amber-500" /> {Math.round(l.consumo_medio_kwh || 0).toLocaleString('pt-BR')} kWh
                                </span>
                            </label>
                        );
                    })}
                    {leadsFiltrados.length === 0 && (
                        <p className="text-center text-sm text-gray-400 py-8">Nenhum lead{vendedoresSel.length ? ' destes responsáveis' : ''} no funil.</p>
                    )}
                </div>
            </div>
        );
    };

    // ── Cards de execução das rotas (dia selecionado + outras rotas ativas) ──
    const renderAvisoNovaVisita = () => avisoNovaVisita && (
        <div className="flex items-center gap-2 rounded-2xl border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20 px-4 py-3">
            <p className="text-xs text-amber-800 dark:text-amber-300 flex-1">
                <b>{avisoNovaVisita.qtd} nova{avisoNovaVisita.qtd > 1 ? 's' : ''} visita{avisoNovaVisita.qtd > 1 ? 's foram adicionadas' : ' foi adicionada'} a hoje.</b> Recalcular rota?
            </p>
            <button onClick={recalcularComNovas} className="text-[11px] font-bold px-3 py-1.5 rounded-full bg-amber-500 text-white hover:bg-amber-600 transition-colors shrink-0">Recalcular</button>
            <button onClick={() => setAvisoNovaVisita(null)} className="p-1 rounded-full text-amber-500 hover:bg-amber-100 dark:hover:bg-amber-900/40 shrink-0"><X size={14} /></button>
        </div>
    );

    const renderCardsExecucao = (lista: RotaAtiva[]) => lista.map(r => (
        <RotaExecucaoCard
            key={r.id}
            rota={r}
            destacar={r.id === rotaDestaqueId}
            onOpenLead={setLeadModalId}
            onIniciarRota={iniciarRota}
            onConcluirRota={concluirRota}
            onCancelarRota={cancelarRota}
            onSalvarObservacoes={salvarObservacoesRota}
            onReordenarParada={reordenarParada}
            onReordenarDaqui={reordenarRotaDaqui}
            onIniciarParada={(p, rota) => isMotoboy
                ? setVisitaModal({ parada: paradaParaInfo(p), etapa: 'iniciar', rota })
                : iniciarVisitaComercial(p)}
            onConcluirParada={(p, rota) => setVisitaModal({ parada: paradaParaInfo(p), etapa: 'encerrar', rota })}
        />
    ));

    // Gestor com vários responsáveis selecionados: resumo somente-leitura das rotas da equipe.
    const renderResumoEquipe = () => (
        <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700 shadow-sm p-5">
            <h2 className="text-sm font-bold text-gray-900 dark:text-white mb-1 flex items-center gap-2">
                <Flag size={15} className="text-emerald-500" />
                Rotas ativas da equipe ({rotasAtivas.length})
            </h2>
            {rotasAtivas.length === 0 ? (
                <p className="text-xs text-gray-400">Nenhuma rota ativa entre os responsáveis selecionados. Ao clicar em <b>Criar Rota Inteligente</b>, será criada <b>uma rota por responsável</b>.</p>
            ) : (
                <div className="space-y-2">
                    <p className="text-[11px] text-gray-400 mb-1">Rotas ativas dos responsáveis selecionados (somente leitura). Clique para ver os detalhes.</p>
                    {rotasAtivas.map(r => {
                        const vis = r.rota_paradas.filter(p => p.status === 'visitado').length;
                        return (
                            <button
                                key={r.id}
                                onClick={() => setDetalheRotaId(r.id)}
                                className="w-full text-left flex items-center justify-between gap-2 p-2.5 rounded-xl bg-gray-50 dark:bg-gray-700/40 border border-transparent hover:border-primary/30 hover:bg-primary/5 transition-all"
                                title="Ver o que aconteceu nesta rota"
                            >
                                <div className="min-w-0">
                                    <p className="text-xs font-semibold text-gray-800 dark:text-gray-100 truncate">{nomeVendedor(r.vendedor_id || '')}</p>
                                    <p className="text-[10px] text-gray-400">
                                        {new Date(r.data + 'T12:00:00').toLocaleDateString('pt-BR')} · {vis}/{r.rota_paradas.length} visitas · ~{Math.round(r.distancia_total_km || 0)} km
                                    </p>
                                </div>
                                <span className={`text-[10px] font-bold px-2 py-1 rounded-full shrink-0 ${statusRotaBadge(r.status)}`}>{statusRotaLabel(r.status)}</span>
                            </button>
                        );
                    })}
                </div>
            )}
        </div>
    );

    if (loading) {
        return <div className="flex items-center justify-center h-64"><LoadingSpinner label="Carregando rotas..." size="sm" /></div>;
    }

    return (
        <div className="space-y-4 px-2 sm:px-0">
            {/* Header */}
            <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="flex items-center gap-3">
                    <div className="p-2.5 bg-emerald-50 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400 rounded-xl">
                        <RouteIcon size={22} />
                    </div>
                    <div>
                        <h1 className="text-lg font-bold text-gray-900 dark:text-white leading-tight">Rotas Comerciais</h1>
                        <p className="text-xs text-gray-500 dark:text-gray-400">Atividades do dia → Rota otimizada → Execução</p>
                    </div>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                    {isManager && (
                        <button
                            onClick={() => setShowApiLog(true)}
                            className="flex items-center gap-2 px-3.5 py-2 text-xs font-bold rounded-full bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-400 hover:bg-emerald-100 dark:hover:bg-emerald-900/40 transition-colors border border-emerald-200 dark:border-emerald-800"
                        >
                            <DollarSign size={14} /> Registro de Uso da API
                        </button>
                    )}
                    {podeGerarParaTerceiros && (
                        <ResponsibleFilter
                            selectedResponsibles={vendedoresSel}
                            setSelectedResponsibles={setVendedoresSel}
                            profiles={profilesCampo}
                            currentUserProfile={profile}
                            label="Minhas rotas"
                            allLabel="Minhas rotas"
                        />
                    )}
                    {tabEfetiva === 'agenda' && (
                        <div className="flex items-center p-1 bg-gray-100 dark:bg-gray-700/50 rounded-full border border-gray-200 dark:border-gray-700">
                            <button
                                onClick={() => { setZoom('semana'); setKpiScope('semana'); }}
                                className={`px-3.5 py-1.5 text-xs font-semibold rounded-full transition-all ${zoom === 'semana' ? 'bg-white dark:bg-gray-600 text-primary shadow-sm' : 'text-gray-400 hover:text-gray-600 dark:hover:text-gray-300'}`}
                            >
                                Semana
                            </button>
                            <button
                                onClick={() => { setZoom('mes'); setKpiScope('mes'); }}
                                className={`px-3.5 py-1.5 text-xs font-semibold rounded-full transition-all ${zoom === 'mes' ? 'bg-white dark:bg-gray-600 text-primary shadow-sm' : 'text-gray-400 hover:text-gray-600 dark:hover:text-gray-300'}`}
                            >
                                Mês
                            </button>
                        </div>
                    )}
                    <div className="flex items-center p-1 bg-gray-100 dark:bg-gray-700/50 rounded-full border border-gray-200 dark:border-gray-700">
                        <button
                            onClick={() => setTab('agenda')}
                            className={`px-4 py-1.5 text-xs font-semibold rounded-full transition-all ${tabEfetiva === 'agenda' ? 'bg-white dark:bg-gray-600 text-primary shadow-sm' : 'text-gray-400 hover:text-gray-600 dark:hover:text-gray-300'}`}
                        >
                            Agenda
                        </button>
                        {isManager && (
                        <button
                            onClick={() => setTab('produtividade')}
                            className={`px-4 py-1.5 text-xs font-semibold rounded-full transition-all ${tabEfetiva === 'produtividade' ? 'bg-white dark:bg-gray-600 text-primary shadow-sm' : 'text-gray-400 hover:text-gray-600 dark:hover:text-gray-300'}`}
                        >
                            Produtividade
                        </button>
                        )}
                        {isManager && (
                        <button
                            onClick={() => setTab('capturas')}
                            className={`px-4 py-1.5 text-xs font-semibold rounded-full transition-all ${tabEfetiva === 'capturas' ? 'bg-white dark:bg-gray-600 text-primary shadow-sm' : 'text-gray-400 hover:text-gray-600 dark:hover:text-gray-300'}`}
                        >
                            Capturas
                        </button>
                        )}
                        {isManager && (
                        <button
                            onClick={() => setTab('aderencia')}
                            className={`px-4 py-1.5 text-xs font-semibold rounded-full transition-all ${tabEfetiva === 'aderencia' ? 'bg-white dark:bg-gray-600 text-primary shadow-sm' : 'text-gray-400 hover:text-gray-600 dark:hover:text-gray-300'}`}
                        >
                            Aderência
                        </button>
                        )}
                        {isManager && (
                        <button
                            onClick={() => setTab('operacional')}
                            className={`px-4 py-1.5 text-xs font-semibold rounded-full transition-all ${tabEfetiva === 'operacional' ? 'bg-white dark:bg-gray-600 text-primary shadow-sm' : 'text-gray-400 hover:text-gray-600 dark:hover:text-gray-300'}`}
                        >
                            Operacional
                        </button>
                        )}
                    </div>
                </div>
            </div>

            {tabEfetiva === 'operacional' && isManager ? (
                <OperacionalDashboard
                    profile={profile}
                    profiles={profiles}
                    vendedorSel={vendedorSelLegacy}
                />
            ) : tabEfetiva === 'aderencia' && isManager ? (
                <AderenciaPanel
                    profile={profile}
                    profiles={profiles}
                    vendedorSel={vendedorSelLegacy}
                />
            ) : tabEfetiva === 'capturas' && isManager ? (
                <CapturasPanel
                    profile={profile}
                    profiles={profiles}
                    vendedorSel={vendedorSelLegacy}
                />
            ) : tabEfetiva === 'produtividade' && isManager ? (
                <ProdutividadeDashboard
                    profile={profile}
                    profiles={profiles}
                    vendedorSel={vendedorSelLegacy}
                    isManager={isManager}
                />
            ) : (
            <>
            {/* Barra de indicadores do gestor (spec §4A) — fixa no topo da Agenda */}
            {isManager && (
                <div className="flex flex-wrap items-center gap-x-5 gap-y-2 rounded-2xl border border-gray-100 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-sm px-4 py-3 mb-4">
                    <div className="flex items-center p-0.5 bg-gray-100 dark:bg-gray-700/50 rounded-full border border-gray-200 dark:border-gray-700 shrink-0">
                        {(['dia', 'semana', 'mes'] as const).map(s => (
                            <button
                                key={s}
                                onClick={() => { setKpiScope(s); setZoom(s === 'mes' ? 'mes' : 'semana'); }}
                                className={`px-3 py-1 text-[11px] font-bold rounded-full transition-all ${kpiScope === s ? 'bg-white dark:bg-gray-600 text-primary shadow-sm' : 'text-gray-400 hover:text-gray-600 dark:hover:text-gray-300'}`}
                            >
                                {s === 'dia' ? 'Dia' : s === 'semana' ? 'Semana' : 'Mês'}
                            </button>
                        ))}
                    </div>
                    <div className="flex flex-wrap items-center gap-x-5 gap-y-1 text-xs">
                        <span className="text-gray-500 dark:text-gray-400">Planejadas {kpiScope === 'dia' ? 'hoje' : kpiScope === 'semana' ? 'na semana' : 'no mês'}: <b className="text-gray-900 dark:text-white">{kpi.planejadas}</b></span>
                        <span className="text-gray-500 dark:text-gray-400">Concluídas: <b className="text-emerald-600 dark:text-emerald-400">{kpi.concluidas}</b> <span className="text-gray-400">({kpi.pct}%)</span></span>
                        <span className="text-gray-500 dark:text-gray-400 flex items-center gap-1"><Zap size={12} className="text-amber-500" /> kWh potencial: <b className="text-amber-600 dark:text-amber-400">{Math.round(kpi.kwhPot).toLocaleString('pt-BR')}</b></span>
                        <span className="text-gray-500 dark:text-gray-400 flex items-center gap-1"><Zap size={12} className="text-emerald-500" /> kWh realizado: <b className="text-emerald-600 dark:text-emerald-400">{Math.round(kpi.kwhReal).toLocaleString('pt-BR')}</b></span>
                        {/* Meta da equipe (adendo §4): vendedores com agenda hoje abaixo das 12 concluídas */}
                        {metaEquipe && metaEquipe.total > 0 && (
                            <span className={`font-bold px-2.5 py-1 rounded-full ${metaEquipe.abaixo > 0 ? 'bg-red-50 text-red-600 dark:bg-red-900/20 dark:text-red-400' : 'bg-emerald-50 text-emerald-600 dark:bg-emerald-900/20 dark:text-emerald-400'}`}>
                                🎯 Abaixo da meta hoje: {metaEquipe.abaixo} de {metaEquipe.total} vendedor{metaEquipe.total > 1 ? 'es' : ''}
                            </span>
                        )}
                    </div>
                </div>
            )}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                {/* Coluna principal */}
                <div className="lg:col-span-2 space-y-4">
                        <>
                            {/* Zoom Mês (visão macro) × Semana (planejamento) — fonte única: visitas planejadas */}
                            {zoom === 'mes' ? (
                                <CalendarioMensal
                                    mes={mesAtual}
                                    onChangeMes={(m) => { setMesAtual(m); setKpiScope('mes'); }}
                                    dataSel={dataSel}
                                    onSelectDate={selecionarData}
                                    resumoPorDia={resumoPorDia}
                                    kmPorDia={kmPorDia}
                                />
                            ) : (
                                <AgendaSemana
                                    leads={leads}
                                    vendedores={vendedoresEfetivos}
                                    vendedorId={vendedorId}
                                    profileId={profile?.id || ''}
                                    dataSel={dataSel}
                                    onSelectDate={selecionarData}
                                    onWeekChange={(iso) => setSemanaExibida(rangeSemana(iso))}
                                    onWeekNavigated={() => setKpiScope('semana')}
                                    onOpenLead={setLeadModalId}
                                    onChanged={() => refreshAll(vendedoresEfetivos)}
                                    totalLeads={leads.length}
                                    reloadSignal={reloadTick}
                                    renderListaCompleta={renderListaLeads}
                                />
                            )}

                            {/* Painel do dia selecionado */}
                            <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700 shadow-sm p-5">
                                <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
                                    <div>
                                        <h2 className="text-sm font-bold text-gray-900 dark:text-white">
                                            {new Date(dataSel + 'T12:00:00').toLocaleDateString('pt-BR', { weekday: 'long', day: '2-digit', month: 'long' })}
                                        </h2>
                                        <p className="text-xs text-gray-500 dark:text-gray-400">
                                            {tarefas.length} atividade{tarefas.length !== 1 ? 's' : ''}
                                            {kwhDoDia > 0 && <> · potencial de <span className="font-bold text-amber-600 dark:text-amber-400">{Math.round(kwhDoDia).toLocaleString('pt-BR')} kWh</span></>}
                                        </p>
                                        {/* Meta com contagem regressiva (adendo §4): 12 concluídas/dia */}
                                        {metaHoje && !isMotoboy && (
                                            <span className={`mt-1.5 inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-bold ${metaChipClass(metaHoje.concluidas)}`}>
                                                🎯 {metaHoje.faltam === 0
                                                    ? `Meta do dia batida! ${metaHoje.concluidas}/${META_CONCLUIDAS_DIA}`
                                                    : `Faltam ${metaHoje.faltam} para a meta · ${metaHoje.concluidas}/${META_CONCLUIDAS_DIA} concluídas`}
                                            </span>
                                        )}
                                    </div>
                                    {!isMotoboy && (
                                        rotaDoDia && rotaDoDia.status === 'em_execucao' ? (
                                            // Rota em execução: o botão vira indicador de progresso (spec §2)
                                            <div
                                                className="flex items-center gap-2 px-4 py-2 text-sm font-bold rounded-full bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 border border-blue-200 dark:border-blue-800"
                                                title="Rota em andamento — conclua as paradas no card à direita"
                                            >
                                                <span className="w-2 h-2 rounded-full bg-blue-500 animate-pulse" />
                                                Rota em execução — {rotaDoDiaProgresso!.feitas}/{rotaDoDiaProgresso!.total} concluídas
                                            </div>
                                        ) : rotaDoDia ? (
                                            // Rota já planejada: recalcula sozinha quando novas visitas entram (spec §2)
                                            <div
                                                className="flex items-center gap-2 px-4 py-2 text-sm font-bold rounded-full bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-800"
                                                title="Rota criada para este dia — inicie pelo card à direita. Novas visitas entram automaticamente."
                                            >
                                                <Navigation size={15} /> Rota planejada — {rotaDoDiaProgresso!.total} parada{rotaDoDiaProgresso!.total !== 1 ? 's' : ''}
                                            </div>
                                        ) : (
                                            <button
                                                onClick={criarRotaInteligenteDoDia}
                                                disabled={gerando || tarefasComLocal === 0}
                                                className="flex items-center gap-2 px-4 py-2 text-sm font-bold rounded-full bg-primary text-white shadow-sm hover:opacity-90 transition-opacity disabled:opacity-40"
                                                title={tarefasComLocal === 0 ? 'Nenhuma visita do dia com lead geolocalizado' : 'Otimiza as visitas do dia a partir da sua localização'}
                                            >
                                                <Navigation size={15} /> {gerando ? 'Gerando...' : `Criar Rota Inteligente (${tarefasComLocal})`}
                                            </button>
                                        )
                                    )}
                                </div>

                                {/* Reforço a partir do horário de corte (adendo §4): ainda dá tempo de agir */}
                                {metaHoje && !isMotoboy && metaHoje.faltam > 0 && metaHoje.aposCorte && (
                                    <p className="text-[11px] font-bold text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl px-3 py-2 mb-3">
                                        ⏰ Já passou das {HORA_CORTE_META}h e faltam <b>{metaHoje.faltam}</b> visita{metaHoje.faltam > 1 ? 's' : ''} para a meta de {META_CONCLUIDAS_DIA}.
                                    </p>
                                )}

                                {(tarefasSemNegociacao > 0 || tarefasSemLocal > 0) && (
                                    <p className="text-[11px] text-amber-600 dark:text-amber-400 mb-3">
                                        {tarefasSemNegociacao > 0 && `${tarefasSemNegociacao} sem negociação vinculada (não roteirizável). `}
                                        {tarefasSemLocal > 0 && `${tarefasSemLocal} com lead sem localização — geolocalize no Mapa Inteligente.`}
                                    </p>
                                )}

                                {tarefas.length === 0 ? (
                                    <div className="text-center py-8 text-gray-400">
                                        <Calendar size={36} className="mx-auto mb-3 opacity-30" />
                                        <p className="text-sm font-semibold text-gray-500 dark:text-gray-400">Nenhuma atividade nesta data{vendedoresSel.length ? ' para este(s) responsável(is)' : ''}.</p>
                                        <p className="text-xs mt-1 max-w-sm mx-auto">Crie a visita no card do lead (Kanban → lead → criar tarefa), escolha o <b>Tipo da Visita</b> e a <b>Data da Visita</b> — ela aparece aqui e no calendário.</p>
                                    </div>
                                ) : (
                                    <div className="space-y-2 mt-2">
                                        {!isMotoboy && (
                                            <div className="flex items-center justify-between pb-1">
                                                <label className="flex items-center gap-2 cursor-pointer text-xs font-semibold text-gray-500 dark:text-gray-400 select-none">
                                                    <input
                                                        type="checkbox"
                                                        checked={todasSelecionadas}
                                                        ref={el => { if (el) el.indeterminate = algumasSelecionadas; }}
                                                        onChange={toggleSelTodas}
                                                        className="rounded text-primary focus:ring-primary"
                                                    />
                                                    Selecionar todos
                                                </label>
                                                {tarefasSel.length > 0 && (
                                                    <button onClick={limparSel} className="text-xs font-semibold text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">Limpar seleção</button>
                                                )}
                                            </div>
                                        )}
                                        {visitasDia.map((t, i) => renderTarefa(t, i))}
                                        {ligacoesDoDia.length > 0 && (
                                            <div className="pt-3 mt-1 border-t border-dashed border-gray-200 dark:border-gray-700">
                                                <p className="text-[11px] font-bold text-purple-600 dark:text-purple-400 flex items-center gap-1.5 mb-2">
                                                    <Phone size={12} /> Remotas do dia ({ligacoesDoDia.length})
                                                </p>
                                                <div className="space-y-2">
                                                    {ligacoesDoDia.map((t, i) => renderTarefa(t, i))}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        </>
                </div>

                {/* Rotas ativas (unificado): as do dia selecionado primeiro, depois as demais */}
                <div className="space-y-4">
                    {multi ? (
                        renderResumoEquipe()
                    ) : (
                        <>
                            {/* Aviso não-bloqueante: nova visita entrou numa rota já em execução (spec §3.2) */}
                            {renderAvisoNovaVisita()}
                            {rotasDoDiaSel.length === 0 ? (
                                <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700 shadow-sm p-5">
                                    <h2 className="text-sm font-bold text-gray-900 dark:text-white mb-1 flex items-center gap-2">
                                        <Flag size={15} className="text-emerald-500" /> Nenhuma rota neste dia
                                    </h2>
                                    <p className="text-xs text-gray-400">
                                        Clique em <b>Criar Rota Inteligente</b> para gerar a rota do dia.
                                    </p>
                                </div>
                            ) : renderCardsExecucao(rotasDoDiaSel)}
                            {outrasRotasAtivas.length > 0 && (
                                <>
                                    <p className="text-[11px] font-bold text-gray-400 uppercase tracking-widest flex items-center gap-1.5 pt-1">
                                        <Flag size={12} /> Outras rotas ativas ({outrasRotasAtivas.length})
                                    </p>
                                    {renderCardsExecucao(outrasRotasAtivas)}
                                </>
                            )}
                        </>
                    )}
                </div>
            </div>
            </>
            )}

            {/* Barra de ações rápidas — atividades do dia selecionadas */}
            {tabEfetiva === 'agenda' && !isMotoboy && tarefasSel.length > 0 && !bulkAcao && (
                <div className="fixed inset-x-0 bottom-0 z-[1000] px-3 pb-3 pointer-events-none flex justify-center">
                    <div className="pointer-events-auto w-full sm:w-auto bg-gray-900 dark:bg-gray-950 text-white rounded-2xl shadow-2xl border border-white/10 px-3 py-2.5 flex flex-wrap items-center gap-2 sm:gap-3 animate-fade-in">
                        <div className="flex items-center gap-2 pr-1">
                            <span className="flex items-center justify-center w-7 h-7 rounded-full bg-primary text-white text-xs font-bold shrink-0">{tarefasSel.length}</span>
                            <div className="leading-tight">
                                <p className="text-xs font-bold whitespace-nowrap">{tarefasSel.length === 1 ? '1 lead selecionado' : `${tarefasSel.length} leads selecionados`}</p>
                                {kwhSel > 0 && (
                                    <p className="text-[10px] text-amber-300 font-semibold flex items-center gap-0.5 whitespace-nowrap">
                                        <Zap size={10} /> Potencial: {Math.round(kwhSel).toLocaleString('pt-BR')} kWh
                                    </p>
                                )}
                            </div>
                        </div>
                        <div className="h-7 w-px bg-white/15 hidden sm:block" />
                        <button onClick={() => setBulkAcao('data')} className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold bg-white/10 hover:bg-white/20 transition-colors">
                            <CalendarClock size={14} /> Alterar Data
                        </button>
                        {isManager && (
                            <button onClick={() => setBulkAcao('vendedor')} className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold bg-white/10 hover:bg-white/20 transition-colors">
                                <UserPlus size={14} /> Atribuir
                            </button>
                        )}
                        <button onClick={() => setBulkAcao('remover')} className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold text-red-300 bg-white/5 hover:bg-red-500/20 transition-colors">
                            <Trash2 size={14} /> Remover
                        </button>
                        <button
                            onClick={criarRotaInteligenteSelecao}
                            disabled={gerando || bulkSalvando || selRoteirizaveisCount === 0}
                            title={selRoteirizaveisCount === 0 ? 'Nenhum lead selecionado com localização' : ''}
                            className="flex items-center gap-1.5 px-4 py-1.5 rounded-full text-xs font-bold bg-primary text-white hover:opacity-90 transition-opacity disabled:opacity-40"
                        >
                            <Navigation size={14} /> Criar Rota Inteligente ({selRoteirizaveisCount})
                        </button>
                        <button onClick={limparSel} className="p-1.5 rounded-full text-white/70 hover:bg-white/10 transition-colors" title="Limpar seleção">
                            <X size={16} />
                        </button>
                    </div>
                </div>
            )}

            {bulkAcao === 'data' && (
                <AlterarDataModal
                    count={tarefasSel.length}
                    dataAtual={dataSel}
                    salvando={bulkSalvando}
                    onConfirm={alterarDataSelecionadas}
                    onClose={() => setBulkAcao(null)}
                />
            )}

            {reagendarVisita && (
                <ReagendarVisitaPopover
                    titulo={reagendarVisita.tarefa.negociacoes?.titulo || reagendarVisita.tarefa.titulo}
                    dataAtual={dataSel}
                    anchor={reagendarVisita.anchor}
                    salvando={reagendandoInline}
                    onConfirm={(novaData) => reagendarVisitaInline(reagendarVisita.tarefa, novaData)}
                    onClose={() => setReagendarVisita(null)}
                />
            )}

            {bulkAcao === 'vendedor' && (
                <AtribuirVendedorModal
                    count={tarefasSel.length}
                    profiles={profilesCampo}
                    currentVendedorId={vendedorId}
                    salvando={bulkSalvando}
                    onConfirm={atribuirVendedorSelecionadas}
                    onClose={() => setBulkAcao(null)}
                />
            )}

            <ConfirmModal
                isOpen={bulkAcao === 'remover'}
                onClose={() => setBulkAcao(null)}
                onConfirm={removerDaRotaSelecionadas}
                loading={bulkSalvando}
                variant="danger"
                title="Remover do roteiro"
                message={`Remover ${tarefasSel.length} visita(s) do roteiro de ${new Date(dataSel + 'T12:00:00').toLocaleDateString('pt-BR')}? Elas saem do dia e do calendário e a ação fica registrada no histórico do lead.`}
                confirmLabel="Remover"
            />

            {visitaModal && (
                <VisitaModal
                    parada={visitaModal.parada}
                    etapa={visitaModal.etapa}
                    modo={modoVisita}
                    onClose={() => setVisitaModal(null)}
                    onIniciar={(info, foto) => iniciarVisita(info, foto, visitaModal.rota)}
                    onEncerrar={(info, dados) => encerrarVisita(info, dados, visitaModal.rota)}
                    onAbrirLead={(id) => { setVisitaModal(null); setLeadModalId(id); }}
                    onChangeModalidade={(m) => mudarModalidadeParada(visitaModal.parada.id, m)}
                />
            )}

            {/* Concluir visita da lista do dia — mesmo painel "Concluir Visita" (observação obrigatória) */}
            {visitaTarefaModal && (
                <VisitaModal
                    parada={tarefaParaInfo(visitaTarefaModal)}
                    etapa="encerrar"
                    modo="comercial"
                    onClose={() => setVisitaTarefaModal(null)}
                    onIniciar={async () => { /* não há check-in numa tarefa de visita */ }}
                    onEncerrar={(_info, dados) => encerrarVisitaTarefa(visitaTarefaModal, dados)}
                    onAbrirLead={(id) => { setVisitaTarefaModal(null); setLeadModalId(id); }}
                    onChangeModalidade={async (m) => {
                        await mudarModalidadeTarefa(visitaTarefaModal.id, m);
                        setVisitaTarefaModal(prev => prev ? { ...prev, modalidade: m } : prev);
                    }}
                />
            )}

            {detalheRotaId && (
                <RotaDetalheModal rotaId={detalheRotaId} onClose={() => setDetalheRotaId(null)} />
            )}

            {/* Abrir o lead a partir da rota/visita (doc 03 §3.1) — mesmo modal de detalhe do Kanban */}
            <NegotiationDetailModal
                isOpen={leadModalId !== null}
                negotiationId={leadModalId}
                onClose={() => setLeadModalId(null)}
                onUpdate={() => refreshAll(vendedoresEfetivos)}
            />

            {/* Concluir rota com pendências → reagendar (doc 03 §5) */}
            {reagendarRota && (
                <div className="fixed inset-0 z-[2000] flex items-end sm:items-center justify-center sm:p-4 bg-black/50" onClick={() => setReagendarRota(null)}>
                    <div className="bg-white dark:bg-gray-800 rounded-t-2xl sm:rounded-2xl shadow-2xl w-full sm:max-w-md overflow-hidden" onClick={e => e.stopPropagation()}>
                        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 dark:border-gray-700">
                            <div className="flex items-center gap-2.5">
                                <div className="p-2 rounded-xl bg-orange-50 dark:bg-orange-900/30 text-orange-600 dark:text-orange-400"><CalendarClock size={18} /></div>
                                <div>
                                    <h2 className="text-sm font-bold text-gray-900 dark:text-white">{reagendarRota.pendentes.length} visita(s) não concluída(s)</h2>
                                    <p className="text-[11px] text-gray-500 dark:text-gray-400">Reagendar para não perder nenhuma?</p>
                                </div>
                            </div>
                            <button onClick={() => setReagendarRota(null)} className="p-1.5 rounded-full text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700"><X size={18} /></button>
                        </div>
                        <div className="p-5 space-y-4">
                            <div className="grid grid-cols-3 gap-1.5">
                                {[{ label: 'Amanhã', value: addDiasIso(1) }, { label: 'Depois de amanhã', value: addDiasIso(2) }, { label: 'Próxima semana', value: addDiasIso(7) }].map(a => (
                                    <button
                                        key={a.label}
                                        onClick={() => setReagendarData(a.value)}
                                        className={`px-2 py-2 rounded-xl text-[11px] font-semibold border transition-all ${reagendarData === a.value ? 'bg-primary/10 border-primary/40 text-primary' : 'bg-gray-50 dark:bg-gray-700/40 border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:border-gray-300'}`}
                                    >
                                        {a.label}
                                    </button>
                                ))}
                            </div>
                            <div>
                                <label className="text-xs font-bold text-gray-700 dark:text-gray-200 block mb-1.5">Ou escolha a data</label>
                                <input
                                    type="date"
                                    value={reagendarData}
                                    min={hoje()}
                                    onChange={e => setReagendarData(e.target.value)}
                                    className="w-full px-3 py-2.5 text-sm rounded-xl bg-gray-50 dark:bg-gray-700/50 border border-gray-200 dark:border-gray-600 text-gray-800 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-primary/40"
                                />
                            </div>
                            <button
                                onClick={() => finalizarRota(reagendarRota.rota, reagendarRota.pendentes, reagendarData)}
                                disabled={!reagendarData || bulkSalvando}
                                className="w-full flex items-center justify-center gap-2 px-4 py-3 text-sm font-bold rounded-xl bg-primary text-white hover:opacity-90 transition-opacity disabled:opacity-40"
                            >
                                <CheckCircle2 size={16} /> {bulkSalvando ? 'Reagendando...' : `Reagendar p/ ${new Date(reagendarData + 'T12:00:00').toLocaleDateString('pt-BR')} e concluir`}
                            </button>
                            <button
                                onClick={() => finalizarRota(reagendarRota.rota, [])}
                                disabled={bulkSalvando}
                                className="w-full text-center text-xs font-semibold text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 py-1"
                            >
                                Concluir sem reagendar
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <RotasApiLogModal isOpen={showApiLog} onClose={() => setShowApiLog(false)} profiles={profiles} />
        </div>
    );
};

export default RotasView;
